import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { whenStyleReady } from '@/lib/mapStyleReady';
import { useAnimatedPositions, useFeedAnimator } from '@/hooks/useAnimatedPositions';
import useSnappedTrails from '@/hooks/useSnappedTrails';
import { projectOnPolyline, truncatePolylineAtProjection, interpolateAlongPolyline } from '@/lib/snapProject';

// Default maximum distance from the animated marker to the snapped polyline
// for which we accept the projection. Per-org override at
// `org.settings.snapToRoad.thresholdMeters`. Above this, the marker has moved
// past the end of the snapped polyline (or the polyline is stale) and we fall
// through to raw so the marker keeps moving instead of snapping backward.
const DEFAULT_SNAP_PROJECT_MAX_METERS = 50;
import { useSelector, useDispatch } from 'react-redux';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { devicesActions, staffActions } from '@/store';
import { getVehicleStatus, STATUS_CONFIG } from '@/lib/utils';
import { resolveMarkerConfig, getIconDef } from '@/lib/markerIcons';
import { ensureMarkerImages, ensureBadgeImages, ensureGlowBadgeImages, renderCardImage } from '@/lib/markerRenderer';
import { buildLabelStyleIndex, resolveCrowding, resolveLabelClickSelects } from '@/features/map/labelStyle';
import { clusterOptionsFor } from '@/features/map/clusterOptions';
import { FAN_MAX, fanOffsets, fanClickDecision, leavesCoLocated, buildFanFC, nearestFanLeaf } from '@/features/map/clusterFan';
import { CARD_FLEET_CAP, getCardSpec, baseCardId, evictUnusedCards, removeAllCardImages } from '@/features/map/labelCards';
import { buildLabelProps } from '@/features/map/labelFields';
import { selectionCameraMove } from '@/components/map/selectionFollow';
import { VEHICLE_PLAIN_LABEL_FILTER, VEHICLE_CARD_FILTER, CARD_ICON_SIZE, CARD_ICON_OFFSET, CARD_SELECTED_ICON_OFFSET, CARD_LAYER_ID, CARD_SELECTED_LAYER_ID, BADGE_LAYER_VARIANTS, SELECTED_BADGE_LAYER_VARIANTS, GLOW_LAYER_VARIANTS, vehicleBadgeGlowFilter, vehicleBadgeSelectedFilter, vehicleBadgeUnselectedFilter, BADGE_TEXT_SIZE, BADGE_TEXT_MAX_WIDTH, BADGE_PADDING, PLAIN_TEXT_SIZE, LABEL_VISIBLE, labelVisibleTimes, DEFAULT_LABEL_MIN_ZOOM, lowestLabelMinZoom, LABEL_LAYER_IDS, spreadLayout, SPREAD_RADIAL_OFFSET_EM, SPREAD_RADIAL_OFFSET_PLAIN_EM } from '@/components/map/labelLayerFilters';
import useFuelLevels from '@/features/fuel/useFuelLevels';
import useUnits from '@/hooks/useUnits';
import useOrg from '@/hooks/useOrg';
import { useTranslation } from 'react-i18next';
import { selectPOIs } from '@/store/selectors';
import { POI_MAP_ICON_SVGS } from '@/features/pois/poiUtils';
import { groupDevicesIntoVehicles } from '@/features/map/vehicleGrouping';
import {
  buildStaffFeatures,
  SOS_COUNT_CLUSTER_PROPERTY,
  STAFF_SOS_HALO_FILTER,
  CLUSTER_SOS_HALO_FILTER,
} from './staffFeatures';
import HeatmapControl from '@/features/map/HeatmapControl';
import RouteControl from '@/features/map/RouteControl';
import MapLayersControl from '@/features/map/MapLayersControl';
import WeatherChip from '@/features/map/layers/WeatherChip';
import SavedViewsControl from '@/features/map/SavedViewsControl';
import IncidentsLayerControl from '@/features/riskmize/IncidentsLayerControl';
import { SEVERITY_COLORS as RISKMIZE_SEVERITY_COLORS } from '@/features/riskmize/severity';
import { isMapboxConfigured } from '@/lib/mapbox';
import useFeatures from '@/hooks/useFeatures';
import useStatusThresholds from '@/hooks/useStatusThresholds';
import useVehicleStatuses from '@/hooks/useVehicleStatuses';
import useMapLayerPrefs, { getLayerPrefs } from '@/hooks/useMapLayerPrefs';
import useWeatherPrefs from '@/hooks/useWeatherPrefs';
import { getInitialMapStyle, MAPBOX_TOKEN, BASEMAP_STYLE_OPTIONS } from '@/features/map/basemapCatalog';
import { fleetBounds } from '@/features/map/fleetBounds';
import { getAuthToken } from '@/lib/trackizy';
import themeConfig from '../../../config/themes/default.json';

// Derive STATUS_COLORS from STATUS_CONFIG so adding a new status doesn't
// require remembering to update this mirror — that miss bit us on 2026-05-23
// when `pending` was missing here and the map rendered a stretched fallback
// marker. Single source = packages/design-tokens/src/status.ts.
const STATUS_COLORS = Object.fromEntries(
  Object.entries(STATUS_CONFIG).map(([k, v]) => [k, v.color]),
);

const CLUSTER_COLOR = '#3b82f6';
const CLUSTER_OPACITY = 0.9;
const CLUSTER_COUNT_FILTER = ['has', 'point_count'];
const VEHICLE_ICON_SIZE = ['interpolate', ['linear'], ['zoom'], 4, 0.45, 10, 0.7, 16, 1];
// Clickable layers of an open cluster fan (spec 2026-10-30 §3.4).
const FAN_HIT_LAYERS = ['fan-markers', 'fan-ring-icons', 'fan-labels'];
// Only the leaf icons select; labels overlap neighbours and never pick a leaf.
const FAN_LEAF_LAYERS = ['fan-markers', 'fan-ring-icons'];
// A click this close to a leaf that hit no icon (e.g. on its label) is not an
// "empty map" click, so the fan stays open.
const FAN_NEAR_LEAF_PX = 20;
const FAN_CLOSE_DRAG_PX = 50;
// Half-size of the box searched for the fanned cluster after a positions tick.
const FAN_RECHECK_BOX_PX = 24;
// Half-size of the box searched for the cluster hiding the selected vehicle: a
// cluster's centroid can sit up to the cluster radius (50 px) from any leaf.
const FAN_SELECTION_BOX_PX = 60;
// A cluster click fans out instead of zooming when splitting it needs more than
// this zoom (the `vehicles` source's tiles are real up to its maxzoom 23, but
// flying past z18 for a few metres of separation is not worth it).
const FAN_ZOOM_CEILING = 18;

// The open fan's leaf for the selected vehicle (sentinel -1 matches none).
const fanSelectedHaloFilter = (selectedId) => ['all',
  ['==', ['get', 'kind'], 'fan-leaf'],
  ['==', ['get', 'leafKind'], 'vehicle'],
  ['==', ['get', 'id'], selectedId ?? -1],
];

// --- Status badge helpers ---

function hasMaintenanceDue(device) {
  if (!device.attributes) return false;
  const attrs = device.attributes;
  const totalDistance = device.totalDistance || 0;
  const odometerPeriod = attrs['maintenance.odometer.period'];
  const odometerStart = attrs['maintenance.odometer.start'] || 0;
  if (odometerPeriod > 0) {
    const remaining = odometerPeriod - ((totalDistance - odometerStart) % odometerPeriod);
    if (remaining < odometerPeriod * 0.1) return true;
  }
  return false;
}

function isDeviceExpired(device) {
  if (!device.expirationTime) return false;
  return new Date(device.expirationTime) < new Date();
}

/**
 * Create a 20x20 badge image for map marker overlays.
 * Returns { width, height, data: Uint8Array } for MapLibre addImage().
 */
function createBadgeImage(type) {
  const size = 20;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');

  // Badge colors
  const colors = {
    maintenance: '#f59e0b', // amber
    expired: '#94a3b8',     // gray
    sos: '#ef4444',         // red
  };

  // Circle background
  ctx.beginPath();
  ctx.arc(size / 2, size / 2, size / 2 - 1, 0, Math.PI * 2);
  ctx.fillStyle = colors[type] || '#94a3b8';
  ctx.fill();

  // White border
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 1.5;
  ctx.stroke();

  // White icon inside
  ctx.fillStyle = '#ffffff';
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 1.5;

  if (type === 'maintenance') {
    // Simple wrench shape
    ctx.font = 'bold 11px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('🔧', size / 2, size / 2);
    // Fallback: draw a simple wrench with lines
    ctx.beginPath();
    ctx.moveTo(6, 14);
    ctx.lineTo(14, 6);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(13, 7, 2.5, 0, Math.PI * 2);
    ctx.stroke();
  } else if (type === 'expired') {
    // Clock icon
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, 5, 0, Math.PI * 2);
    ctx.stroke();
    // Clock hands
    ctx.beginPath();
    ctx.moveTo(size / 2, size / 2);
    ctx.lineTo(size / 2, size / 2 - 3.5);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(size / 2, size / 2);
    ctx.lineTo(size / 2 + 2.5, size / 2);
    ctx.stroke();
  } else if (type === 'sos') {
    // Exclamation mark
    ctx.font = 'bold 13px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('!', size / 2, size / 2);
  }

  const imageData = ctx.getImageData(0, 0, size, size);
  return { width: size, height: size, data: new Uint8Array(imageData.data.buffer) };
}

/**
 * Draw a directional arrow on a canvas, returning MapLibre-compatible image data.
 * Arrow points UP; MapLibre rotates via icon-rotate.
 */
function createArrowImage(color, size = 36) {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');

  const cx = size / 2;

  // Shadow
  ctx.shadowColor = 'rgba(0,0,0,0.25)';
  ctx.shadowBlur = 3;
  ctx.shadowOffsetY = 1;

  // Arrow body
  ctx.beginPath();
  ctx.moveTo(cx, 4);                // tip
  ctx.lineTo(cx + 11, size - 8);    // bottom-right
  ctx.lineTo(cx, size - 14);        // notch
  ctx.lineTo(cx - 11, size - 8);    // bottom-left
  ctx.closePath();

  ctx.fillStyle = color;
  ctx.fill();

  // White edge for contrast
  ctx.shadowColor = 'transparent';
  ctx.strokeStyle = 'rgba(255,255,255,0.85)';
  ctx.lineWidth = 1.5;
  ctx.stroke();

  return { width: size, height: size, data: new Uint8Array(ctx.getImageData(0, 0, size, size).data) };
}

/**
 * Build GeoJSON from devices + positions. `statuses` is a Map<deviceId, status>
 * computed once by useVehicleStatuses() from RAW (non-animated) positions, so
 * every surface classifies the same way even during animation legs. `thresholds`
 * is no longer used here but kept for signature parity with callers that still
 * pass it; remove once callers are audited.
 */
function buildGeoJSON(devices, positions, thresholds, statuses, labelCtx) {
  const deviceArray = Object.values(devices);

  // Multi-tracker vehicles: car rentals put 2-4 GPS trackers in one car. Group
  // them ONCE per build (never per-device in the loop) so we draw ONE marker per
  // car — folding out the secondary trackers and re-anchoring the primary to the
  // car's best position (primary fix while fresh, else freshest live sibling —
  // the anti-theft fallback). Reuse the returned set/map below.
  const { bestPositionByVehicle, bestDeviceIdByVehicle, secondaryDeviceIds, suspectByVehicle } = groupDevicesIntoVehicles(deviceArray, positions, thresholds);

  const features = deviceArray
    .filter((device) => !device.hidden_from_default_list)
    .map((device) => {
      // A linked-but-secondary tracker emits NO feature — its car is already
      // drawn once at the primary (in addition to the hidden filter above).
      if (secondaryDeviceIds.has(device.id)) return null;

      // A vehicle's primary tracker is positioned at the CAR's best position;
      // fall back to the primary's own fix if the group has none. Unlinked
      // devices (vehicleId == null) are completely unchanged.
      const isPrimary = !!device.vehicleId && device.isPrimary === true;
      const pos = isPrimary
        ? (bestPositionByVehicle.get(device.vehicleId) ?? positions[device.id])
        : positions[device.id];
      if (!pos || (pos.latitude === 0 && pos.longitude === 0)) return null;

      // The marker's status/color must reflect the position we actually render.
      // For a primary drawn at the best (possibly sibling) fix, reclassify from
      // the OWNER of that fix — not the primary device — so a car running on a
      // live sibling shows green instead of colouring Offline just because the
      // pulled primary's `device.status === 'offline'`. Owner falls back to the
      // primary when the group has no fresh owner. Unlinked devices keep the
      // shared statuses map (computed once from raw positions by useVehicleStatuses()).
      let status;
      if (isPrimary) {
        const ownerId = bestDeviceIdByVehicle.get(device.vehicleId);
        const ownerDevice = devices[ownerId] ?? device;
        status = getVehicleStatus(ownerDevice, pos, thresholds);
      } else {
        status = statuses?.get(device.id) ?? 'offline';
      }
      const { style, icon, customUrl } = resolveMarkerConfig(device.attributes);
      const def = icon ? getIconDef(icon) : null;

      let iconImage = `arrow-${status}`;
      let ringIconImage = '';
      let shouldRotate = true;

      if (style === 'colored' && icon && icon !== 'custom') {
        iconImage = `icon-${icon}-${status}`;
        shouldRotate = def?.directional !== false;
      } else if (style === 'colored' && icon === 'custom' && customUrl) {
        iconImage = `device-${device.id}`;
        shouldRotate = true;
      } else if (style === 'ring' && icon && icon !== 'custom') {
        const needsPointer = def?.directional && status !== 'offline';
        iconImage = needsPointer ? `ring-${status}` : `ring-nopointer-${status}`;
        ringIconImage = `ring-icon-${icon}`;
        shouldRotate = def?.directional !== false;
      } else if (style === 'ring' && icon === 'custom' && customUrl) {
        const needsPointer = status !== 'offline';
        iconImage = needsPointer ? `ring-${status}` : `ring-nopointer-${status}`;
        ringIconImage = `device-${device.id}`;
        shouldRotate = true;
      }

      const resolved = labelCtx?.styleIndex?.get(device.id);
      const labelProps = resolved && labelCtx
        ? buildLabelProps(resolved.style, {
            device, position: pos, status, thresholds,
            speedUnit: labelCtx.speedUnit,
            fuelRow: labelCtx.fuelByDevice?.get(device.id),
            t: labelCtx.t,
            crowding: labelCtx.crowding,
          })
        : { labelText: device.name, labelBg: '#ffffff', labelFg: '#334155', labelOpacity: 0.9, labelShape: 'none', labelOverlap: 0 };

      return {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [pos.longitude, pos.latitude],
        },
        properties: {
          kind: 'vehicle',
          id: device.id,
          name: device.name,
          status,
          speed: Number(pos.speed) || 0,
          course: Number(pos.course) || 0,
          color: STATUS_COLORS[status],
          iconImage,
          ringIconImage,
          shouldRotate: shouldRotate ? 1 : 0,
          badgeMaintenance: hasMaintenanceDue(device),
          badgeExpired: isDeviceExpired(device),
          badgeSos: pos?.attributes?.alarm === 'sos',
          // Tamper-divergence: one tracker live while a sibling went silent
          // (likely removal). Only a vehicle primary can be suspect — unlinked
          // devices carry 0. Drives the subtle red `vehicle-suspect-halo` pulse.
          suspect: isPrimary && suspectByVehicle.get(device.vehicleId) ? 1 : 0,
          ...labelProps,
        },
      };
    })
    .filter(Boolean);

  return { type: 'FeatureCollection', features };
}

/**
 * Combine vehicle + staff features into a single FeatureCollection that feeds
 * the unified `vehicles` cluster source. The `kind` property on each feature
 * drives the cluster paint expression (vehicle/staff/mixed) and the
 * per-feature marker layer filters.
 */
function buildCombinedAssetFC(devices, positions, thresholds, staffByUserId, vehiclesEnabled, statuses, labelCtx) {
  const vehicleFeatures = vehiclesEnabled ? buildGeoJSON(devices, positions, thresholds, statuses, labelCtx).features : [];
  const staffFeatures = buildStaffFeatures(staffByUserId ?? {});
  return {
    type: 'FeatureCollection',
    features: [...vehicleFeatures, ...staffFeatures],
  };
}

// buildStaffFeatures lives in ./staffFeatures.js (pure, tested alongside the
// SOS halo filters that depend on its property shape).

// MAP_STYLES moved to basemapCatalog.js — use getInitialMapStyle() for init

function circleToPolygon(lat, lng, radius, steps = 64) {
  const coords = [];
  for (let i = 0; i <= steps; i++) {
    const angle = (i / steps) * 2 * Math.PI;
    const dx = radius * Math.cos(angle);
    const dy = radius * Math.sin(angle);
    const dlng = dx / (111320 * Math.cos((lat * Math.PI) / 180));
    const dlat = dy / 111320;
    coords.push([lng + dlng, lat + dlat]);
  }
  return coords;
}

// Custom device icons: one fetch + one downscale per URL, shared by every
// device that uses it. Before this each device fetched its own copy — one
// 419 KB PNG was pulled five times per Live Map load on staging (2026-10-26)
// because the five requests raced ahead of the browser cache. The decoded
// 36 px bitmap is what MapLibre needs anyway; the full-size image is never
// drawn. Rejections are not cached so a transient failure can retry.
const CUSTOM_ICON_MAX_DIM = 36;
const scaledIconCache = new Map();
function loadScaledIcon(url) {
  const cached = scaledIconCache.get(url);
  if (cached) return cached;
  const promise = new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const scale = CUSTOM_ICON_MAX_DIM / Math.max(img.width, img.height);
      const w = Math.max(1, Math.round(img.width * scale));
      const h = Math.max(1, Math.round(img.height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, w, h);
      resolve({ width: w, height: h, data: new Uint8Array(ctx.getImageData(0, 0, w, h).data) });
    };
    img.onerror = () => reject(new Error(`icon load failed: ${url}`));
    img.src = url;
  });
  promise.catch(() => scaledIconCache.delete(url));
  scaledIconCache.set(url, promise);
  return promise;
}

const MapView = forwardRef(function MapView({ trailData, trailMinZoom = 10, vehiclesEnabled = true, animatePositions = false, suppressSelectionFlyTo = false, followSelection = true, onMapContextMenu, layerTogglesEnabled = false, layersStatus = null, ...props }, ref) {
  const mapContainer = useRef(null);
  const mapRef = useRef(null);
  const updatePoiSourcesRef = useRef(null);
  const applyPoiVisibilityRef = useRef(null);
  const dispatch = useDispatch();
  const loadedIcons = useRef(new Set());
  // Last cluster option pushed to the source (it is created with the stored pref).
  const clusterAppliedRef = useRef({ cluster: getLayerPrefs().cluster, crowding: null });
  // Label layers' zoom floor currently applied (see lowestLabelMinZoom).
  const labelFloorRef = useRef(DEFAULT_LABEL_MIN_ZOOM);
  // Cluster fan-out (spec 2026-10-30 §3.4): `{ clusterId, center, extra, leafKey }`
  // while a fan is open, else null. `fanSeqRef` bumps on every open/close so a
  // late getClusterLeaves answer for an older fan is ignored. `closeFanRef` lets
  // the selection effect close the fan (the function lives in the init effect).
  const fanStateRef = useRef(null);
  const fanSeqRef = useRef(0);
  const fanMoveStartPxRef = useRef(null);
  const closeFanRef = useRef(null);
  // Group mode keeps the selected vehicle visible by fanning the cluster hiding
  // it (`fanForSelectionRef`, set in the init effect). `fanCameraMoveRef` is set
  // while the selection camera moves, so that programmatic zoom/pan does not
  // close the fan; `fanMoveEndRef` is the pending once('moveend') follow-up.
  const fanForSelectionRef = useRef(null);
  const fanCameraMoveRef = useRef(false);
  const fanMoveEndRef = useRef(null);
  // Re-fan the selection once the map settles (crowding switched to Group).
  const refanSelectionRef = useRef(null);
  // Suspect (tamper-divergence) halo pulse — the rAF id for the once-registered
  // breathe loop, and a cheap flag so the loop only repaints while at least one
  // suspect vehicle is on screen (avoids a 60fps repaint on an idle fleet map).
  const suspectPulseRef = useRef(null);
  const suspectPresentRef = useRef(false);
  // SOS halo pulse (MyCRM 233) — driven by the same once-registered rAF loop;
  // this flag gates repaints to frames where a person with an open SOS (or a
  // cluster containing one) is on the map.
  const sosPresentRef = useRef(false);

  // Task 7: traffic/aircraft/liveuamap layer state. Data refs so a basemap
  // switch (which wipes+rebuilds custom sources) can reseed from the last
  // known value inside initializeCustomLayers instead of blanking the layer.
  const EMPTY_FC = { type: 'FeatureCollection', features: [] };
  const trafficVisibleRef = useRef(false);
  const aircraftDataRef = useRef(null);
  const aircraftVisibleRef = useRef(false);
  const aircraftTrailRef = useRef(null);
  const liveuamapDataRef = useRef(null);
  const liveuamapVisibleRef = useRef(false);

  // Weather raster overlay (Task 4) — one OpenWeatherMap layer at a time via
  // the server tile proxy. Ref seeds initializeCustomLayers on basemap switch;
  // the source is only added when a layer is actively selected (unlike
  // traffic, which is always created and merely toggles visibility).
  // Gated on server-reported availability (same finding class as the
  // traffic-layer features.trafficLayer gate): a stale localStorage tileLayer
  // in an org whose weather flag was revoked (or key removed) must not keep
  // issuing tile requests that 403 — the radio group hides when unavailable,
  // so there'd be no UI to turn it off. Availability restored → the user's
  // saved pref reapplies automatically.
  const weatherPrefs = useWeatherPrefs();
  const weatherActive = layersStatus?.weather?.available ? weatherPrefs.tileLayer : 'off';
  const weatherTileLayerRef = useRef(weatherActive);

  // Keep latest callback in a ref — the map-init useEffect runs once and
  // captures closure-time props, so a stale handler would otherwise persist.
  const onMapContextMenuRef = useRef(onMapContextMenu);
  useEffect(() => { onMapContextMenuRef.current = onMapContextMenu; }, [onMapContextMenu]);

  useImperativeHandle(ref, () => ({
    getMap: () => mapRef.current,
    setHeatmapData: (geojson) => {
      const map = mapRef.current;
      if (!map) return;
      const source = map.getSource('heatmap-data');
      if (source) source.setData(geojson || { type: 'FeatureCollection', features: [] });
      if (map.getLayer('heatmap-layer')) {
        map.setLayoutProperty('heatmap-layer', 'visibility', geojson?.features?.length ? 'visible' : 'none');
      }
    },
    setHeatmapIntensity: (value) => {
      const map = mapRef.current;
      if (map?.getLayer('heatmap-layer')) {
        map.setPaintProperty('heatmap-layer', 'heatmap-intensity', value);
      }
    },
    // Riskmize incidents — GeoJSON FeatureCollection of Point features with
    // properties.{id, severity, ...}. Visibility is controlled separately so
    // the layer can be hidden without clearing the data buffer.
    setIncidentsData: (fc) => {
      const map = mapRef.current;
      if (!map) return;
      const source = map.getSource('incidents');
      if (source) source.setData(fc ?? { type: 'FeatureCollection', features: [] });
    },
    setIncidentsVisible: (visible) => {
      const map = mapRef.current;
      if (map?.getLayer('incidents-markers')) {
        map.setLayoutProperty('incidents-markers', 'visibility', visible ? 'visible' : 'none');
      }
    },
    // Aircraft — GeoJSON pushed by AircraftOverlay (Task 8). Data survives a
    // basemap switch via aircraftDataRef, reseeded in initializeCustomLayers.
    setAircraftData: (fc) => {
      aircraftDataRef.current = fc ?? null;
      const source = mapRef.current?.getSource('aircraft');
      if (source) source.setData(fc ?? EMPTY_FC);
    },
    setAircraftVisible: (visible) => {
      aircraftVisibleRef.current = Boolean(visible);
      const map = mapRef.current;
      if (map?.getLayer('aircraft-markers')) {
        map.setLayoutProperty('aircraft-markers', 'visibility', visible ? 'visible' : 'none');
      }
    },
    // Premium flight trail — one selected flight's path (AircraftPanel pushes
    // it). Survives a basemap switch via aircraftTrailRef, reseeded in
    // initializeCustomLayers.
    setAircraftTrail: (fc) => {
      aircraftTrailRef.current = fc ?? null;
      const source = mapRef.current?.getSource('aircraft-trail');
      if (source) source.setData(fc ?? EMPTY_FC);
    },
    // Liveuamap incidents — GeoJSON pushed by LiveuamapOverlay (Task 8).
    setLiveuamapData: (fc) => {
      liveuamapDataRef.current = fc ?? null;
      const source = mapRef.current?.getSource('liveuamap');
      if (source) source.setData(fc ?? EMPTY_FC);
    },
    setLiveuamapVisible: (visible) => {
      liveuamapVisibleRef.current = Boolean(visible);
      const map = mapRef.current;
      if (map?.getLayer('liveuamap-markers')) {
        map.setLayoutProperty('liveuamap-markers', 'visibility', visible ? 'visible' : 'none');
      }
    },
    // Staff phones — synthetic devices with uniqueid LIKE 'staff:%'. Rendered
    // separately from vehicle markers so they can be toggled independently.
    // Deprecated: staff features now flow into the unified `vehicles` source
    // via Redux. Kept as a no-op so legacy callers (if any) don't crash.
    setStaffData: () => {},
    setStaffVisible: (visible) => {
      const map = mapRef.current;
      if (!map) return;
      const vis = visible ? 'visible' : 'none';
      if (map.getLayer('staff-markers')) {
        map.setLayoutProperty('staff-markers', 'visibility', vis);
      }
      if (map.getLayer('staff-labels')) {
        map.setLayoutProperty('staff-labels', 'visibility', vis);
      }
      if (map.getLayer('staff-sos-halo')) {
        map.setLayoutProperty('staff-sos-halo', 'visibility', vis);
      }
    },
  }));

  // Internal API ref for HeatmapControl to call setHeatmapData/setHeatmapIntensity
  const heatmapApiRef = useRef(null);

  useEffect(() => {
    heatmapApiRef.current = {
      setHeatmapData: (geojson) => {
        const map = mapRef.current;
        if (!map) return;
        const source = map.getSource('heatmap-data');
        if (source) source.setData(geojson || { type: 'FeatureCollection', features: [] });
        if (map.getLayer('heatmap-layer')) {
          map.setLayoutProperty('heatmap-layer', 'visibility', geojson?.features?.length ? 'visible' : 'none');
        }
      },
      setHeatmapIntensity: (value) => {
        const map = mapRef.current;
        if (map?.getLayer('heatmap-layer')) {
          map.setPaintProperty('heatmap-layer', 'heatmap-intensity', value);
        }
      },
    };
  }, []);

  const features = useFeatures();
  const showRouteControl = features.routeCalculator && isMapboxConfigured();
  const { layers: layerPrefs } = useMapLayerPrefs();

  // Internal API ref for RouteControl to manage route layers
  const routeApiRef = useRef(null);
  const routeAnimationRef = useRef(null);
  const routeGeoJSONRef = useRef(null); // Cache GeoJSON for highlightRoute
  const routeMarkersRef = useRef({ start: null, end: null }); // Draggable MapLibre markers

  // Animation helpers (plain functions, not hooks — called from routeApiRef)
  const startRouteAnimation = () => {
    if (routeAnimationRef.current) return;
    let dashOffset = 0;
    const animate = () => {
      const map = mapRef.current;
      if (!map || !map.getLayer('route-primary')) {
        routeAnimationRef.current = null;
        return;
      }
      dashOffset = (dashOffset + 1) % 24;
      map.setPaintProperty('route-primary', 'line-dasharray', [
        Math.max(0.1, 2 - dashOffset / 12),
        Math.max(0.1, 2 + dashOffset / 12),
      ]);
      routeAnimationRef.current = requestAnimationFrame(animate);
    };
    routeAnimationRef.current = requestAnimationFrame(animate);
  };

  const stopRouteAnimation = () => {
    if (routeAnimationRef.current) {
      cancelAnimationFrame(routeAnimationRef.current);
      routeAnimationRef.current = null;
    }
  };

  const removeRouteMarkers = () => {
    if (routeMarkersRef.current.start) {
      routeMarkersRef.current.start.remove();
      routeMarkersRef.current.start = null;
    }
    if (routeMarkersRef.current.end) {
      routeMarkersRef.current.end.remove();
      routeMarkersRef.current.end = null;
    }
  };

  useEffect(() => {
    routeApiRef.current = {
      setRouteData: (geojson) => {
        const map = mapRef.current;
        if (!map) return;
        routeGeoJSONRef.current = geojson;
        const source = map.getSource('route-data');
        if (source) {
          source.setData(geojson || { type: 'FeatureCollection', features: [] });
        }
        const hasData = geojson?.features?.length > 0;
        ['route-primary', 'route-alternative'].forEach((id) => {
          if (map.getLayer(id)) {
            map.setLayoutProperty(id, 'visibility', hasData ? 'visible' : 'none');
          }
        });
        if (hasData) {
          startRouteAnimation();
        } else {
          stopRouteAnimation();
        }
      },
      clearRoute: () => {
        const map = mapRef.current;
        if (!map) return;
        routeGeoJSONRef.current = null;
        const source = map.getSource('route-data');
        if (source) source.setData({ type: 'FeatureCollection', features: [] });
        ['route-primary', 'route-alternative'].forEach((id) => {
          if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', 'none');
        });
        stopRouteAnimation();
        removeRouteMarkers();
      },
      highlightRoute: (index) => {
        const map = mapRef.current;
        if (!map) return;
        const source = map.getSource('route-data');
        const data = routeGeoJSONRef.current;
        if (!source || !data?.features) return;
        const updated = {
          ...data,
          features: data.features.map((f) => ({
            ...f,
            properties: { ...f.properties, selected: f.properties.index === index },
          })),
        };
        routeGeoJSONRef.current = updated;
        source.setData(updated);
      },
      setRouteMarkers: ({ from, to, onDragEnd }) => {
        const map = mapRef.current;
        if (!map) return;
        removeRouteMarkers();

        if (from) {
          const el = document.createElement('div');
          el.className = 'route-marker-start';
          el.style.cssText = 'width:16px;height:16px;border-radius:50%;background:#10b981;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3);cursor:grab;';
          const marker = new maplibregl.Marker({ element: el, draggable: true })
            .setLngLat([from.lng, from.lat])
            .addTo(map);
          marker.on('dragend', () => {
            const lngLat = marker.getLngLat();
            onDragEnd?.('from', { lng: lngLat.lng, lat: lngLat.lat });
          });
          routeMarkersRef.current.start = marker;
        }

        if (to) {
          const el = document.createElement('div');
          el.className = 'route-marker-end';
          el.style.cssText = 'width:16px;height:16px;border-radius:50%;background:#ef4444;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3);cursor:grab;';
          const marker = new maplibregl.Marker({ element: el, draggable: true })
            .setLngLat([to.lng, to.lat])
            .addTo(map);
          marker.on('dragend', () => {
            const lngLat = marker.getLngLat();
            onDragEnd?.('to', { lng: lngLat.lng, lat: lngLat.lat });
          });
          routeMarkersRef.current.end = marker;
        }
      },
      updateStartMarker: (lngLat) => {
        if (routeMarkersRef.current.start) {
          routeMarkersRef.current.start.setLngLat([lngLat.lng, lngLat.lat]);
        }
      },
      fitRouteBounds: (geojson) => {
        const map = mapRef.current;
        if (!map || !geojson?.features?.length) return;
        const coords = [];
        geojson.features.forEach((f) => {
          if (f.geometry?.coordinates) {
            coords.push(...f.geometry.coordinates);
          }
        });
        if (coords.length < 2) return;
        const bounds = coords.reduce(
          (b, c) => {
            b[0][0] = Math.min(b[0][0], c[0]);
            b[0][1] = Math.min(b[0][1], c[1]);
            b[1][0] = Math.max(b[1][0], c[0]);
            b[1][1] = Math.max(b[1][1], c[1]);
            return b;
          },
          [[Infinity, Infinity], [-Infinity, -Infinity]]
        );
        map.fitBounds(bounds, {
          padding: { top: 80, bottom: 80, left: 320, right: 300 },
          duration: 1000,
        });
      },
      getMap: () => mapRef.current,
    };
  }, []);

  const [showPOIs, setShowPOIs] = useState(false);
  const devices = useSelector((state) => state.devices.items);
  const positions = useSelector((state) => state.session.positions);
  const thresholds = useStatusThresholds();
  const { t } = useTranslation();
  const groupsById = useSelector((state) => state.groups.items);
  const orgSettings = useOrg()?.settings;
  const { speedUnit } = useUnits();
  const { byDevice: fuelByDevice } = useFuelLevels();
  const labelStyleIndex = useMemo(
    () => buildLabelStyleIndex(devices, groupsById, orgSettings),
    [devices, groupsById, orgSettings],
  );
  // Single classification per render across all surfaces. See
  // src/hooks/useVehicleStatuses.js for the rationale.
  const vehicleStatuses = useVehicleStatuses();

  // Inter-fix marker animation — feeds the engine from Redux and exposes a
  // smoothed snapshot. When `animatePositions` is off, the engine is reset
  // and `effectivePositions` falls through to Redux unchanged.
  useFeedAnimator(positions, animatePositions);
  const animatedSnapshot = useAnimatedPositions();
  // Snap-to-road overlay (org-flag gated). When on, the hook fans out batched
  // /api/snap/match calls and exposes the latest matched fix per device. We
  // overlay those on top of the animated fix so the marker sits on the road
  // network instead of the GPS-jitter cloud. Off → empty Map, fallthrough.
  const snapTrails = useSnappedTrails();
  // The hook already clamps + falls back, so this is just a non-null guard
  // for the (impossible) case where the hook surface changes.
  const snapThresholdMeters = Number.isFinite(snapTrails.thresholdM)
    ? snapTrails.thresholdM
    : DEFAULT_SNAP_PROJECT_MAX_METERS;
  const effectivePositions = useMemo(() => {
    const animatedActive = animatePositions && animatedSnapshot.size > 0;
    const snapActive = snapTrails.enabled && snapTrails.polylineById.size > 0;
    if (!animatedActive && !snapActive) return positions;
    const merged = { ...positions };
    if (animatedActive) {
      for (const [id, p] of animatedSnapshot) merged[id] = p;
    }
    if (snapActive) {
      // Phase 3 of the snap-to-road plan: project the (animated-or-raw)
      // marker position onto the snapped polyline OSRM returned, instead of
      // overlaying the last snapped fix. This lets the marker glide smoothly
      // along the road between OSRM batches AND keeps marker + trail glued
      // together. When the marker has moved past the polyline's end (next
      // batch hasn't landed yet), the projection distance exceeds the cap
      // and we fall through to raw — marker keeps moving, will re-snap on
      // the next batch.
      for (const [idStr, base] of Object.entries(merged)) {
        const id = Number(idStr);
        const polyline = snapTrails.polylineById.get(id);
        if (!polyline) continue;
        // Phase 3 animation: while the marker is mid-tween, walk it ALONG the
        // road between the two fixes instead of straight-lerp-then-project, so
        // it rounds curves instead of cutting corners. Falls back below when
        // either fix is past the snap cap (e.g. OSRM batch lag).
        if (base._animPrev && base._animTarget && typeof base._animFraction === 'number') {
          const along = interpolateAlongPolyline(base._animPrev, base._animTarget, base._animFraction, polyline, snapThresholdMeters);
          if (along) {
            merged[id] = { ...base, latitude: along.latitude, longitude: along.longitude };
            continue;
          }
        }
        const projected = projectOnPolyline(base.latitude, base.longitude, polyline);
        if (!projected || projected.distanceMeters > snapThresholdMeters) continue;
        // Keep speed/course/fixTime from the live position so status logic
        // (moving/stopped/idle) is unaffected — only the lat/lng moves.
        merged[id] = { ...base, latitude: projected.latitude, longitude: projected.longitude };
      }
    }
    return merged;
  }, [positions, animatedSnapshot, animatePositions, snapTrails.enabled, snapTrails.polylineById, snapTrails.tick, snapThresholdMeters]);
  // "Stack as cards" bakes one image per label; above CARD_FLEET_CAP vehicles on
  // the map it falls back to Spread out (spec 2026-10-30 §3.3). The count is a
  // number, so the crowding memo (and labelCtx) only change when it crosses the cap.
  const vehicleCount = useMemo(() => {
    let n = 0;
    for (const id of Object.keys(devices)) if (effectivePositions[id]) n += 1;
    return n;
  }, [devices, effectivePositions]);
  const crowding = useMemo(() => {
    const mode = resolveCrowding(orgSettings);
    return mode === 'stack' && vehicleCount > CARD_FLEET_CAP ? 'spread' : mode;
  }, [orgSettings, vehicleCount]);
  const crowdingRef = useRef(crowding);
  crowdingRef.current = crowding;
  // Org option: a click on a vehicle's label selects it like a click on its marker.
  const labelClickSelectsRef = useRef(false);
  labelClickSelectsRef.current = resolveLabelClickSelects(orgSettings);
  // The fan's "+N more" label is built inside once-registered map handlers.
  const tRef = useRef(t);
  tRef.current = t;
  const labelCtx = useMemo(
    () => ({ styleIndex: labelStyleIndex, speedUnit, fuelByDevice, t, crowding }),
    [labelStyleIndex, speedUnit, fuelByDevice, t, crowding],
  );
  const selectedId = useSelector((state) => state.devices.selectedId);
  // Read by the fan's selected-leaf halo when a basemap switch rebuilds it.
  const selectedIdRef = useRef(selectedId);
  selectedIdRef.current = selectedId;
  const selectedStaffUserId = useSelector((state) => state.staff?.selectedUserId);
  const staffByUserId = useSelector((state) => state.staff?.byUserId ?? {});
  const server = useSelector((state) => state.session.server);
  const poiList = useSelector(selectPOIs);
  // Org admin's basemap baseline. Threaded into getInitialMapStyle so the
  // initial tile style honors an admin pick when the user hasn't made one
  // in localStorage yet. MapLayersControl itself also reads this to render
  // any org-defined custom basemap entry.
  const orgBasemap = useSelector((s) => s.org?.settings?.basemap) || null;
  // Platform-level basemaps (sultan-curated, instance-wide). Threaded into
  // getInitialMapStyle alongside orgBasemap so a user's saved platform-basemap
  // pick resolves on init instead of silently falling back after reload.
  const platformBasemaps = useSelector((s) => s.session?.platformBasemaps);

  const { center, zoom } = themeConfig.mapDefaults;
  // Set further down (after the positions selectors); the init effect calls
  // through the ref so declaration order doesn't matter.
  const tryFleetFitRef = useRef(null);

  // Initialize map
  useEffect(() => {
    if (mapRef.current) return;

    // No `style` in the constructor: MapOptions has no transformStyle hook, so
    // the initial style goes through setStyle() below, where
    // BASEMAP_STYLE_OPTIONS strips the hillshade raster (see basemapCatalog).
    const map = new maplibregl.Map({
      container: mapContainer.current,
      center: [center[0], center[1]],
      zoom: zoom,
      attributionControl: false,
      // Label flicker fix: MapLibre matches a symbol across source updates by
      // its text + anchor within ~2 px (CrossTileSymbolIndex). A moving vehicle
      // (15 Hz animator emits, or a plain WS fix) or a changing value in the
      // badge text gets a NEW symbol id, whose opacity starts at 0 and fades in
      // over `fadeDuration` (default 300 ms) while the old one fades out — seen
      // as labels flickering/ghosting on moving vehicles. With 0 the new
      // instance paints at full opacity on the same frame.
      fadeDuration: 0,
      // Weather raster tiles ride the server proxy behind JWT auth, but
      // MapLibre's own tile fetches never carry an Authorization header.
      // Attach the Bearer token only to weather-tile URLs — everything else
      // (basemap tiles, Mapbox traffic) returns undefined so MapLibre uses
      // its default fetch behavior unmodified.
      transformRequest: (url) => {
        if (url.startsWith(window.location.origin + '/api/layers/weather/tile/') || url.startsWith('/api/layers/weather/tile/')) {
          return { url, headers: { Authorization: `Bearer ${getAuthToken() ?? ''}` } };
        }
        return undefined;
      },
    });

    map.setStyle(getInitialMapStyle({ orgDefault: orgBasemap?.default, orgBasemap, platformBasemaps }), BASEMAP_STYLE_OPTIONS);

    map.addControl(new maplibregl.AttributionControl({ compact: true }), 'bottom-right');

    // Basemap styles (e.g. OpenFreeMap) reference Maki sprite icons such as
    // "circle-11" that their served sprite occasionally omits, spamming the
    // console with "image could not be loaded" warnings on every map view.
    // Register a 1×1 transparent placeholder for any missing image so the
    // warning is silenced — those are basemap POI glyphs we don't render.
    map.on('styleimagemissing', (e) => {
      const id = e.id;
      // Label cards (`card-*`) are rendered by the card handler registered below
      // ("Label cards are rendered on demand"); a placeholder here would win the
      // race and every card would be a 1×1 transparent pixel.
      if (!id || id.startsWith('card-') || map.hasImage(id)) return;
      map.addImage(id, { width: 1, height: 1, data: new Uint8Array(4) });
    });

    // Extract layer/source setup so it can be re-called after style changes
    const initializeCustomLayers = () => {
      // Reset loaded icons tracker — style change removes all images
      loadedIcons.current = new Set();
      // A rebuild re-creates `fan` empty and `clusters` at full opacity.
      fanStateRef.current = null;
      fanSeqRef.current += 1;

      // Idempotency sweep — `style.load` can fire on the same map without the
      // previous custom sources/layers being fully cleared (rapid theme flips,
      // React strict-mode double-effect, MutationObserver-driven setStyle).
      // Without this, `addSource('trails', …)` throws "Source already exists",
      // and the throw aborts the rest of the layer setup → vehicle markers
      // silently disappear from the map. Remove our known custom sources
      // (and any layers attached to them) before re-adding.
      const customSourceIds = ['vehicles', 'poi-circles', 'pois', 'trails', 'heatmap-data', 'route-data', 'incidents', 'traffic', 'aircraft', 'aircraft-trail', 'liveuamap', 'weather-tiles', 'fan'];
      const existingStyle = map.getStyle();
      for (const layer of (existingStyle?.layers ?? [])) {
        if (customSourceIds.includes(layer.source) && map.getLayer(layer.id)) {
          map.removeLayer(layer.id);
        }
      }
      for (const id of customSourceIds) {
        if (map.getSource(id)) map.removeSource(id);
      }

      // Traffic — Mapbox congestion vector tiles. Added first so vehicles,
      // POIs, and trails always draw above the congestion lines.
      if (MAPBOX_TOKEN) {
        map.addSource('traffic', {
          type: 'vector',
          tiles: [`https://api.mapbox.com/v4/mapbox.mapbox-traffic-v1/{z}/{x}/{y}.vector.pbf?access_token=${MAPBOX_TOKEN}`],
          minzoom: 0,
          maxzoom: 14,
        });
        map.addLayer({
          id: 'traffic-lines',
          type: 'line',
          source: 'traffic',
          'source-layer': 'traffic',
          layout: {
            visibility: trafficVisibleRef.current ? 'visible' : 'none',
            'line-cap': 'round',
            'line-join': 'round',
          },
          paint: {
            'line-color': ['match', ['get', 'congestion'],
              'low', '#22c55e', 'moderate', '#f59e0b', 'heavy', '#f97316', 'severe', '#991b1b',
              '#94a3b8'],
            'line-width': ['interpolate', ['linear'], ['zoom'], 6, 0.5, 10, 1.5, 14, 3, 18, 6],
            'line-opacity': 0.8,
          },
        });
      }

      // Weather raster overlay — one OWM layer at a time via the server
      // proxy. Only added when a layer is actively selected (unlike traffic,
      // which is always created); switching/toggling is handled by the
      // dedicated tileLayer effect below, which also re-adds this on a
      // basemap switch by way of weatherTileLayerRef.
      const wxLayer = weatherTileLayerRef.current;
      if (wxLayer && wxLayer !== 'off') {
        map.addSource('weather-tiles', {
          type: 'raster',
          tiles: [`${window.location.origin}/api/layers/weather/tile/${wxLayer}/{z}/{x}/{y}`],
          tileSize: 256,
          maxzoom: 12,
        });
        map.addLayer({
          id: 'weather-tiles-layer', type: 'raster', source: 'weather-tiles',
          paint: { 'raster-opacity': 0.65 },
        });
      }

      // Generate arrow images for each status color
      for (const [status, color] of Object.entries(STATUS_COLORS)) {
        if (!map.hasImage(`arrow-${status}`)) {
          map.addImage(`arrow-${status}`, createArrowImage(color));
        }
      }

      // Unified asset source — vehicles AND staff phones in one cluster
      // source so a single bubble counts both kinds. Composition is encoded
      // via clusterProperties + a 3-color paint expression below.
      clusterAppliedRef.current = { cluster: getLayerPrefs().cluster, crowding: crowdingRef.current };
      map.addSource('vehicles', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
        // Real tiles up to z23 so Group mode's clusters (clusterMaxZoom 22) are
        // re-clustered at z19–22 instead of stretched z18 copies.
        maxzoom: 23,
        ...clusterOptionsFor({ clusterEnabled: getLayerPrefs().cluster, crowding: crowdingRef.current }),
        clusterProperties: {
          vehicle_count: ['+', ['case', ['==', ['get', 'kind'], 'vehicle'], 1, 0]],
          staff_count:   ['+', ['case', ['==', ['get', 'kind'], 'staff'],   1, 0]],
          sos_count:     SOS_COUNT_CLUSTER_PROPERTY,
        },
      });

      // SOS halo behind a cluster that hides a person with an open SOS — a
      // cluster must not be able to swallow an emergency. Added BEFORE the
      // cluster circle so it draws underneath; pulsed by the rAF loop below.
      map.addLayer({
        id: 'cluster-sos-halo',
        type: 'circle',
        source: 'vehicles',
        filter: CLUSTER_SOS_HALO_FILTER,
        paint: {
          'circle-radius': 40,
          'circle-color': '#ef4444',
          'circle-opacity': 0.25,
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ef4444',
          'circle-stroke-opacity': 0.5,
          'circle-blur': 0.5,
        },
      });

      // Cluster circles — color by composition:
      //   contains SOS → red    (#ef4444) — wins over every other hue
      //   all vehicles → green  (#10b981)
      //   all staff    → blue   (#3b82f6)
      //   mixed        → purple (#8b5cf6)
      // Distinct hues so users can decode at a glance without hovering.
      map.addLayer({
        id: 'clusters',
        type: 'circle',
        source: 'vehicles',
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': [
            'case',
            ['>',  ['get', 'sos_count'],     0], '#ef4444',
            ['==', ['get', 'vehicle_count'], 0], '#3b82f6',
            ['==', ['get', 'staff_count'],   0], '#10b981',
            '#8b5cf6',
          ],
          'circle-radius': ['step', ['get', 'point_count'], 18, 10, 22, 50, 28],
          'circle-opacity': CLUSTER_OPACITY,
          'circle-stroke-width': 3,
          'circle-stroke-color': [
            'case',
            ['>',  ['get', 'sos_count'],     0], '#ef4444',
            ['==', ['get', 'vehicle_count'], 0], '#3b82f6',
            ['==', ['get', 'staff_count'],   0], '#10b981',
            '#8b5cf6',
          ],
          'circle-stroke-opacity': 0.2,
        },
      });

      // Cluster count labels
      map.addLayer({
        id: 'cluster-count',
        type: 'symbol',
        source: 'vehicles',
        filter: CLUSTER_COUNT_FILTER,
        layout: {
          'text-field': '{point_count_abbreviated}',
          'text-size': 12,
          'text-font': ['Noto Sans Bold'],
          'text-allow-overlap': true,
        },
        paint: {
          'text-color': '#ffffff',
        },
      });

      // Selection halo behind the vehicle marker — soft blue glow filtered to
      // the currently-selected device id. Larger radius + blur so the glow
      // visually wraps a vehicle name label too if/when one is shown.
      map.addLayer({
        id: 'vehicle-selection-halo',
        type: 'circle',
        source: 'vehicles',
        filter: ['all',
          ['!', ['has', 'point_count']],
          ['==', ['get', 'kind'], 'vehicle'],
          ['==', ['get', 'id'], -1],
        ],
        paint: {
          'circle-radius': 32,
          'circle-color': '#3b82f6',
          'circle-opacity': 0.14,
          'circle-stroke-width': 2,
          'circle-stroke-color': '#3b82f6',
          'circle-stroke-opacity': 0.45,
          'circle-blur': 0.7,
        },
      });

      // Suspect (tamper-divergence) halo behind the vehicle marker — a subtle
      // red glow on cars where one tracker is live while a sibling went silent
      // (likely removal). Added BEFORE vehicle-markers so it sits behind them.
      // The gentle breathe is driven by the once-registered rAF loop below
      // (paint props here are just the resting values it animates around); it
      // is intentionally softer than the SOS badge / blue selection halo.
      map.addLayer({
        id: 'vehicle-suspect-halo',
        type: 'circle',
        source: 'vehicles',
        filter: ['all',
          ['!', ['has', 'point_count']],
          ['==', ['get', 'kind'], 'vehicle'],
          ['==', ['get', 'suspect'], 1],
        ],
        paint: {
          'circle-radius': 26,
          'circle-color': '#dc2626',
          'circle-opacity': 0.10,
          'circle-stroke-width': 2,
          'circle-stroke-color': '#dc2626',
          'circle-stroke-opacity': 0.25,
          'circle-blur': 0.6,
        },
      });

      // Vehicle arrow/icon markers (unclustered)
      map.addLayer({
        id: 'vehicle-markers',
        type: 'symbol',
        source: 'vehicles',
        filter: ['all',
          ['!', ['has', 'point_count']],
          ['==', ['get', 'kind'], 'vehicle'],
        ],
        layout: {
          'icon-image': ['get', 'iconImage'],
          'icon-size': VEHICLE_ICON_SIZE,
          'icon-rotate': [
            'case',
            ['==', ['get', 'shouldRotate'], 1],
            ['get', 'course'],
            0
          ],
          'icon-rotation-alignment': 'map',
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
        },
      });

      // Ring icon overlay layer — renders the white icon that stays upright
      map.addLayer({
        id: 'vehicle-ring-icons',
        type: 'symbol',
        source: 'vehicles',
        filter: ['all',
          ['!', ['has', 'point_count']],
          ['==', ['get', 'kind'], 'vehicle'],
          ['has', 'ringIconImage'],
          ['!=', ['get', 'ringIconImage'], ''],
        ],
        layout: {
          'icon-image': ['get', 'ringIconImage'],
          'icon-size': [
            'interpolate', ['linear'], ['zoom'],
            4, 0.45,
            10, 0.7,
            16, 1,
          ],
          'icon-rotate': 0,
          'icon-rotation-alignment': 'viewport',
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
        },
      });

      // Vehicle name labels — plain halo text (label style shape 'none').
      map.addLayer({
        id: 'vehicle-labels',
        type: 'symbol',
        source: 'vehicles',
        filter: VEHICLE_PLAIN_LABEL_FILTER,
        layout: {
          'text-field': ['get', 'name'],
          'text-size': PLAIN_TEXT_SIZE,
          'text-font': ['Noto Sans Bold'],
          ...spreadLayout(SPREAD_RADIAL_OFFSET_PLAIN_EM),
          'text-max-width': 10,
          'text-allow-overlap': false,
        },
        paint: {
          'text-color': '#334155',
          'text-opacity': LABEL_VISIBLE,
          'text-halo-color': '#ffffff',
          'text-halo-width': 1.5,
        },
        // The per-style minimum zoom (default 12) is applied per feature by the
        // expressions above; the layer floor is the lowest a style may choose.
        minzoom: labelFloorRef.current,
      });

      // Vehicle label badges — a 9-slice pill/rect stretched around the text
      // (spec docs/superpowers/specs/2026-10-14-map-label-styles-design.md §5).
      // `icon-text-fit-padding` is data-constant in the style spec, so the compact
      // padding is a layer variant (BADGE_LAYER_VARIANTS), not a per-feature flag.
      // Badges never overlap: overlapping labels ("Stack as cards") are the card
      // layers below. Badges spread to another side of the marker before hiding;
      // the glow and the selected vehicle's badge pass `spread: false` and keep the
      // fixed top anchor, so the two always coincide (SELECTED_BADGE_LAYER_VARIANTS).
      const badgeLayer = (id, filter, { padding, spread = true }) => ({
        id,
        type: 'symbol',
        source: 'vehicles',
        filter,
        layout: {
          'text-field': ['get', 'labelText'],
          'text-size': BADGE_TEXT_SIZE,
          'text-font': ['Noto Sans Bold'],
          'text-max-width': BADGE_TEXT_MAX_WIDTH,
          'text-justify': 'center',
          ...(spread
            ? spreadLayout(SPREAD_RADIAL_OFFSET_EM)
            : { 'text-offset': [0, 2.2], 'text-anchor': 'top', 'icon-anchor': 'top' }),
          'text-line-height': 1.25,
          'text-allow-overlap': false,
          'icon-image': ['concat', 'label-badge-', ['get', 'labelShape'], '-',
            ['slice', ['downcase', ['get', 'labelBg']], 1]],
          'icon-text-fit': 'both',
          'icon-text-fit-padding': padding,
          'icon-allow-overlap': false,
        },
        paint: {
          'text-color': ['get', 'labelFg'],
          'text-opacity': LABEL_VISIBLE,
          'icon-opacity': labelVisibleTimes(['get', 'labelOpacity']),
        },
        minzoom: labelFloorRef.current,
      });
      // Selection glow: a larger blue badge drawn BENEATH the badge layers
      // (added first), filtered to the selected vehicle by the selection effect.
      // One glow layer per padding (normal / compact); overlap flags are literals.
      ensureGlowBadgeImages(map);
      // Fixed placement: MapLibre cannot share a chosen anchor across layers, so the
      // selected badge is its own fixed-anchor layer (below) rather than a spread one.
      for (const glow of GLOW_LAYER_VARIANTS) {
        const glowBase = badgeLayer(glow.id, vehicleBadgeGlowFilter(null, glow.tight), { padding: glow.padding, spread: false });
        map.addLayer({
          ...glowBase,
          layout: {
            ...glowBase.layout,
            'icon-image': ['concat', 'label-badge-', ['get', 'labelShape'], '-', '3b82f6'],
            'text-allow-overlap': true,
            'icon-allow-overlap': true,
            'icon-ignore-placement': true,
          },
          paint: { 'text-opacity': 0, 'icon-opacity': labelVisibleTimes(0.55) },
        });
      }
      for (const v of BADGE_LAYER_VARIANTS) map.addLayer(badgeLayer(v.id, v.filter, { padding: v.padding }));
      // The selected vehicle's badge: fixed top anchor over its glow, always shown, drawn
      // above the spread badges; the selection effect moves the id between the filters.
      for (const v of SELECTED_BADGE_LAYER_VARIANTS) {
        const base = badgeLayer(v.id, vehicleBadgeSelectedFilter(null, v.tight), { padding: v.padding, spread: false });
        map.addLayer({ ...base, layout: { ...base.layout, 'text-allow-overlap': true, 'icon-allow-overlap': true } });
      }

      // "Stack as cards" (spec 2026-10-30 §3.3): each overlapping label is one baked
      // image (`labelCard`, rendered lazily by the `styleimagemissing` handler), so
      // the layer is icon-only — no text pass. Cards are baked opaque, so a card on
      // top fully hides the one beneath; `viewport-y` draws the card lower on screen
      // on top (also under rotation/pitch). CARD_ICON_OFFSET puts the card's top
      // edge where the spread badge's top edge is, per text-size preset.
      const cardLayer = (id, filter, iconImage, iconOffset) => ({
        id,
        type: 'symbol',
        source: 'vehicles',
        filter,
        layout: {
          'icon-image': iconImage,
          'icon-anchor': 'top',
          'icon-offset': iconOffset,
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'symbol-z-order': 'viewport-y',
          'icon-size': CARD_ICON_SIZE,
        },
        paint: { 'icon-opacity': LABEL_VISIBLE },
        minzoom: labelFloorRef.current,
      });
      map.addLayer(cardLayer(CARD_LAYER_ID, VEHICLE_CARD_FILTER, ['get', 'labelCard'], CARD_ICON_OFFSET));
      // The selected card, on top of every other card. Its image is 2 px larger per
      // side (the blue ring), so it sits 2 px higher to keep the fill aligned.
      map.addLayer(cardLayer(CARD_SELECTED_LAYER_ID, ['all', VEHICLE_CARD_FILTER, ['==', ['get', 'id'], -1]],
        ['concat', 'card-sel-', ['slice', ['get', 'labelCard'], 5]], CARD_SELECTED_ICON_OFFSET));

      // --- Status badge images + layer ---
      ['maintenance', 'expired', 'sos'].forEach((type) => {
        const imageId = `badge-${type}`;
        if (!map.hasImage(imageId)) {
          map.addImage(imageId, createBadgeImage(type), { sdf: false });
        }
      });

      map.addLayer({
        id: 'vehicle-badges',
        type: 'symbol',
        source: 'vehicles',
        filter: ['all',
          ['!', ['has', 'point_count']],
          ['any',
            ['==', ['get', 'badgeMaintenance'], true],
            ['==', ['get', 'badgeExpired'], true],
            ['==', ['get', 'badgeSos'], true],
          ],
        ],
        layout: {
          'icon-image': [
            'case',
            ['==', ['get', 'badgeSos'], true], 'badge-sos',
            ['==', ['get', 'badgeExpired'], true], 'badge-expired',
            ['==', ['get', 'badgeMaintenance'], true], 'badge-maintenance',
            '',
          ],
          'icon-size': 1,
          'icon-offset': [12, -12],
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
        },
      });

      // Cluster click (zoom in or fan out) is registered ONCE below, outside
      // this rebuild function.

      // A fan leaf can sit over an unclustered marker; the fan click handler
      // selects the leaf, so the marker handlers stand down.
      const fanLeafAt = (point) => {
        const layers = FAN_HIT_LAYERS.filter((id) => map.getLayer(id));
        return layers.length > 0 && map.queryRenderedFeatures(point, { layers }).length > 0;
      };

      // Click handler: vehicle -> select (and clear any staff selection)
      map.on('click', 'vehicle-markers', (e) => {
        if (fanLeafAt(e.point)) return; // an open fan's leaf drawn over this marker wins
        const feature = e.features[0];
        if (feature) {
          dispatch(staffActions.clearSelection());
          dispatch(devicesActions.selectId(feature.properties.id));
        }
      });

      // Cursor changes
      map.on('mouseenter', 'vehicle-markers', () => { map.getCanvas().style.cursor = 'pointer'; });
      map.on('mouseleave', 'vehicle-markers', () => { map.getCanvas().style.cursor = ''; });

      // Org option "clicking a label selects the vehicle" (spec 2026-10-30 §3.6):
      // every label layer selects like the marker does. Registered after the marker
      // handler, so a label lying over another vehicle's marker wins. Off = a label
      // click stays an empty-map click (see INTERACTIVE_LAYERS below).
      for (const id of LABEL_LAYER_IDS) {
        map.on('click', id, (e) => {
          if (!labelClickSelectsRef.current) return;
          if (fanLeafAt(e.point)) return;
          const feature = e.features[0];
          if (!feature || feature.properties.kind !== 'vehicle') return;
          dispatch(staffActions.clearSelection());
          dispatch(devicesActions.selectId(feature.properties.id));
        });
        map.on('mouseenter', id, () => { if (labelClickSelectsRef.current) map.getCanvas().style.cursor = 'pointer'; });
        map.on('mouseleave', id, () => { if (labelClickSelectsRef.current) map.getCanvas().style.cursor = ''; });
      }

      // Click + cursor handlers for ring icon overlay layer
      map.on('click', 'vehicle-ring-icons', (e) => {
        if (fanLeafAt(e.point)) return;
        const feature = e.features[0];
        if (feature) {
          dispatch(staffActions.clearSelection());
          dispatch(devicesActions.selectId(feature.properties.id));
        }
      });
      map.on('mouseenter', 'vehicle-ring-icons', () => { map.getCanvas().style.cursor = 'pointer'; });
      map.on('mouseleave', 'vehicle-ring-icons', () => { map.getCanvas().style.cursor = ''; });

      // Staff marker selection — mirrors the vehicle click pattern. Mutual
      // exclusion with vehicle selection: dispatching staffActions.selectUser
      // also clears devices.selectedId so the bottom panel doesn't try to
      // render both contexts.
      const selectStaffFromFeature = (e) => {
        const feature = e.features?.[0];
        if (!feature) return;
        const userId = feature.properties.user_id;
        if (!userId) return;
        dispatch(devicesActions.selectId(null));
        dispatch(staffActions.selectUser(userId));
      };
      map.on('click', 'staff-markers', selectStaffFromFeature);
      map.on('click', 'staff-labels', selectStaffFromFeature);
      map.on('mouseenter', 'staff-markers', () => { map.getCanvas().style.cursor = 'pointer'; });
      map.on('mouseleave', 'staff-markers', () => { map.getCanvas().style.cursor = ''; });
      map.on('mouseenter', 'staff-labels', () => { map.getCanvas().style.cursor = 'pointer'; });
      map.on('mouseleave', 'staff-labels', () => { map.getCanvas().style.cursor = ''; });

      // Right-click (contextmenu) — single global handler with explicit
      // priority. Per-layer handlers would double-fire when markers overlap
      // (POI sitting on top of a vehicle marker, etc.). Priority order:
      // POI > vehicle > staff > empty map.
      const POI_LAYERS = ['poi-markers', 'poi-icon-symbols', 'poi-labels', 'assembly-points-layer'];
      const VEHICLE_LAYERS = ['vehicle-markers', 'vehicle-ring-icons'];
      const STAFF_LAYERS = ['staff-markers', 'staff-labels'];
      const fireContextMenu = (kind, payload, e) => {
        const cb = onMapContextMenuRef.current;
        if (!cb) return;
        cb({
          kind,
          x: e.originalEvent?.clientX ?? 0,
          y: e.originalEvent?.clientY ?? 0,
          lngLat: { lng: e.lngLat.lng, lat: e.lngLat.lat },
          payload,
        });
      };
      map.on('contextmenu', (e) => {
        e.preventDefault?.();
        const allAssetLayers = [...POI_LAYERS, ...VEHICLE_LAYERS, ...STAFF_LAYERS]
          .filter((id) => map.getLayer(id));
        const features = allAssetLayers.length
          ? map.queryRenderedFeatures(e.point, { layers: allAssetLayers })
          : [];
        const poiHit = features.find((f) => POI_LAYERS.includes(f.layer.id));
        if (poiHit) return fireContextMenu('poi', { poiId: poiHit.properties.id }, e);
        const vehicleHit = features.find((f) => VEHICLE_LAYERS.includes(f.layer.id));
        if (vehicleHit) return fireContextMenu('vehicle', { deviceId: vehicleHit.properties.id }, e);
        const staffHit = features.find((f) => STAFF_LAYERS.includes(f.layer.id));
        if (staffHit) return fireContextMenu('staff', { userId: staffHit.properties.user_id }, e);
        fireContextMenu('map', {}, e);
      });

      // Empty-map click deselects whatever vehicle/staff is currently selected.
      // The per-layer click handlers above always run first (MapLibre fires
      // layer-filtered handlers before generic ones), so we only need to
      // detect "click hit no asset feature." Layers that have their own click
      // behavior (clusters zoom in, incidents open a drawer, route-alternative
      // swaps the polyline) are included in the no-deselect set so clicking
      // them doesn't surprise-clear the panel.
      const INTERACTIVE_LAYERS = [
        ...POI_LAYERS, ...VEHICLE_LAYERS, ...STAFF_LAYERS,
        'clusters', 'incidents-markers', 'route-alternative',
        'aircraft-markers', 'liveuamap-markers',
        ...FAN_HIT_LAYERS,
      ];
      map.on('click', (e) => {
        const interactive = labelClickSelectsRef.current ? [...INTERACTIVE_LAYERS, ...LABEL_LAYER_IDS] : INTERACTIVE_LAYERS;
        const layers = interactive.filter((id) => map.getLayer(id));
        const hit = layers.length
          ? map.queryRenderedFeatures(e.point, { layers })
          : [];
        if (hit.length > 0) return;
        dispatch(devicesActions.selectId(null));
        dispatch(staffActions.clearSelection());
      });

      // Load POI icon images
      Object.entries(POI_MAP_ICON_SVGS).forEach(([name, svg]) => {
        const img = new Image(20, 20);
        img.onload = () => {
          if (!map.hasImage(`poi-icon-${name}`)) {
            map.addImage(`poi-icon-${name}`, img);
          }
        };
        img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
      });

      // Aircraft sprite (points north; rotated per-feature by `track`)
      const AIRCRAFT_SVG = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path d="M12 1.5c.5 0 .9.35 1.05.83L14.5 8.5l7 4.2c.3.18.5.5.5.86v1.14l-7.6-1.9-.5 5.4 2.35 1.76c.16.12.25.3.25.5V22l-4.5-1.1L7.5 22v-1.54c0-.2.09-.38.25-.5l2.35-1.76-.5-5.4L2 14.7v-1.14c0-.35.2-.68.5-.86l7-4.2 1.45-6.17c.15-.48.55-.83 1.05-.83z" fill="#0ea5e9" stroke="#ffffff" stroke-width="1.2"/></svg>';
      {
        const img = new Image(24, 24);
        img.onload = () => {
          if (!map.hasImage('aircraft-icon')) map.addImage('aircraft-icon', img);
        };
        img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(AIRCRAFT_SVG);
      }

      // POI radius circles source + layers
      map.addSource('poi-circles', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });

      map.addLayer({
        id: 'poi-circle-fill',
        type: 'fill',
        source: 'poi-circles',
        paint: { 'fill-color': ['get', 'color'], 'fill-opacity': 0.1 },
        layout: { visibility: 'none' },
      });

      map.addLayer({
        id: 'poi-circle-border',
        type: 'line',
        source: 'poi-circles',
        paint: { 'line-color': ['get', 'color'], 'line-width': 1.5, 'line-opacity': 0.4 },
        layout: { visibility: 'none' },
      });

      // POI point markers source
      map.addSource('pois', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });

      map.addLayer({
        id: 'poi-markers',
        type: 'circle',
        source: 'pois',
        paint: {
          'circle-radius': 10,
          'circle-color': ['get', 'color'],
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff',
        },
        layout: { visibility: 'none' },
      });

      // POI icon symbols (white icons on colored circles)
      map.addLayer({
        id: 'poi-icon-symbols',
        type: 'symbol',
        source: 'pois',
        layout: {
          'icon-image': ['concat', 'poi-icon-', ['get', 'icon']],
          'icon-size': 0.55,
          'icon-allow-overlap': true,
          visibility: 'none',
        },
      });

      map.addLayer({
        id: 'poi-labels',
        type: 'symbol',
        source: 'pois',
        layout: {
          'text-field': ['get', 'name'],
          'text-size': 11,
          'text-offset': [0, 1.5],
          'text-anchor': 'top',
          visibility: 'none',
        },
        paint: { 'text-color': '#475569', 'text-halo-color': '#ffffff', 'text-halo-width': 1.5 },
      });

      // Assembly point markers — distinct red flag style (featureChat gate)
      if (features.chat) {
        // Create assembly point icon (red circle with white flag)
        const assemblyCanvas = document.createElement('canvas');
        assemblyCanvas.width = 32;
        assemblyCanvas.height = 32;
        const actx = assemblyCanvas.getContext('2d');
        // Red circle background
        actx.beginPath();
        actx.arc(16, 16, 14, 0, Math.PI * 2);
        actx.fillStyle = '#ef4444';
        actx.fill();
        actx.strokeStyle = '#ffffff';
        actx.lineWidth = 2;
        actx.stroke();
        // White flag icon
        actx.fillStyle = '#ffffff';
        actx.beginPath();
        actx.moveTo(11, 8);
        actx.lineTo(11, 24);
        actx.lineTo(13, 24);
        actx.lineTo(13, 18);
        actx.lineTo(22, 14);
        actx.lineTo(13, 10);
        actx.lineTo(13, 8);
        actx.closePath();
        actx.fill();
        if (!map.hasImage('assembly-point-icon')) {
          const aImgData = actx.getImageData(0, 0, 32, 32);
          map.addImage('assembly-point-icon', { width: 32, height: 32, data: new Uint8Array(aImgData.data) });
        }

        map.addLayer({
          id: 'assembly-points-layer',
          type: 'symbol',
          source: 'pois',
          filter: ['==', ['get', 'category_slug'], 'assembly_point'],
          layout: {
            'icon-image': 'assembly-point-icon',
            'icon-size': 1.2,
            'icon-allow-overlap': true,
            'text-field': ['get', 'name'],
            'text-offset': [0, 1.5],
            'text-anchor': 'top',
            'text-size': 11,
            visibility: 'none',
          },
          paint: {
            'text-color': '#ef4444',
            'text-halo-color': '#ffffff',
            'text-halo-width': 1,
          },
        });
      }

      // Trails source (for vehicle movement trails)
      map.addSource('trails', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });

      // Trails line layer — green lines behind vehicles, only visible when zoomed in
      map.addLayer({
        id: 'trails-line',
        type: 'line',
        source: 'trails',
        minzoom: trailMinZoom,
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
          'visibility': 'visible',
        },
        paint: {
          'line-width': 4,
          'line-color': '#10b981',
          'line-opacity': 0.7,
        },
      });

      // Heatmap source (empty initially)
      map.addSource('heatmap-data', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });

      // Heatmap layer — insert below vehicle-markers
      map.addLayer({
        id: 'heatmap-layer',
        type: 'heatmap',
        source: 'heatmap-data',
        layout: { visibility: 'none' },
        paint: {
          'heatmap-weight': ['get', 'weight'],
          'heatmap-intensity': 0.75,
          'heatmap-radius': [
            'interpolate', ['linear'], ['zoom'],
            4, 8, 10, 20, 16, 40,
          ],
          'heatmap-color': [
            'interpolate', ['linear'], ['heatmap-density'],
            0, 'rgba(0,0,0,0)',
            0.2, 'rgba(59,130,246,0.4)',
            0.4, 'rgba(59,130,246,0.6)',
            0.6, 'rgba(245,158,11,0.7)',
            0.8, 'rgba(239,68,68,0.8)',
            1, 'rgba(239,68,68,0.9)',
          ],
          'heatmap-opacity': 0.7,
        },
      }, 'vehicle-markers');

      // Route calculator source (empty initially)
      map.addSource('route-data', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });

      // Alternative route lines — insert below vehicle-markers
      map.addLayer({
        id: 'route-alternative',
        type: 'line',
        source: 'route-data',
        filter: ['==', ['get', 'selected'], false],
        layout: { visibility: 'none', 'line-join': 'round', 'line-cap': 'round' },
        paint: {
          'line-color': '#94a3b8',
          'line-width': 4,
          'line-opacity': 0.4,
        },
      }, 'vehicle-markers');

      // Primary route line (animated dashed) — insert below vehicle-markers
      map.addLayer({
        id: 'route-primary',
        type: 'line',
        source: 'route-data',
        filter: ['==', ['get', 'selected'], true],
        layout: { visibility: 'none', 'line-join': 'round', 'line-cap': 'round' },
        paint: {
          'line-color': '#3b82f6',
          'line-width': 6,
          'line-opacity': 0.85,
          'line-dasharray': [2, 2],
        },
      }, 'vehicle-markers');

      // Click handler: alternative route -> select it
      map.on('click', 'route-alternative', (e) => {
        const feature = e.features?.[0];
        if (feature) {
          const event = new CustomEvent('route-alt-click', { detail: { index: feature.properties.index } });
          document.dispatchEvent(event);
        }
      });

      // Cursor on alternative routes
      map.on('mouseenter', 'route-alternative', () => { map.getCanvas().style.cursor = 'pointer'; });
      map.on('mouseleave', 'route-alternative', () => { map.getCanvas().style.cursor = ''; });

      // ─── Riskmize incidents source + layer ─────────────────────────────────
      // Circles only — Riskmize doesn't expose polygons (Phase 0 lock-in).
      map.addSource('incidents', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });

      map.addLayer({
        id: 'incidents-markers',
        type: 'circle',
        source: 'incidents',
        layout: { visibility: 'none' },
        paint: {
          'circle-color': [
            'match', ['get', 'severity'],
            'critical', RISKMIZE_SEVERITY_COLORS.critical,
            'high',     RISKMIZE_SEVERITY_COLORS.high,
            'medium',   RISKMIZE_SEVERITY_COLORS.medium,
            'low',      RISKMIZE_SEVERITY_COLORS.low,
            /* default */ RISKMIZE_SEVERITY_COLORS.low,
          ],
          'circle-radius': [
            'match', ['get', 'severity'],
            'critical', 10,
            'high',     8,
            'medium',   7,
            'low',      6,
            /* default */ 6,
          ],
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff',
        },
      });

      // Click: compute screen anchor + dispatch custom DOM event on the map container.
      // LiveMapPage (map host) listens for 'riskmize:incident-click' to position the popup.
      map.on('click', 'incidents-markers', (e) => {
        const feature = e.features?.[0];
        if (!feature) return;
        const point = map.project(e.lngLat);
        map.getContainer().dispatchEvent(
          new CustomEvent('riskmize:incident-click', {
            detail: {
              id: feature.properties.id,
              anchor: { x: point.x, y: point.y },
            },
          }),
        );
      });

      // Hover cursor
      map.on('mouseenter', 'incidents-markers', () => { map.getCanvas().style.cursor = 'pointer'; });
      map.on('mouseleave', 'incidents-markers', () => { map.getCanvas().style.cursor = ''; });

      // Aircraft — GeoJSON pushed by AircraftOverlay via setAircraftData.
      // Seeded from the ref so a basemap switch doesn't blank the layer.
      map.addSource('aircraft', { type: 'geojson', data: aircraftDataRef.current ?? EMPTY_FC });
      map.addLayer({
        id: 'aircraft-markers',
        type: 'symbol',
        source: 'aircraft',
        layout: {
          visibility: aircraftVisibleRef.current ? 'visible' : 'none',
          'icon-image': 'aircraft-icon',
          'icon-size': 0.9,
          'icon-rotate': ['get', 'track'],
          'icon-rotation-alignment': 'map',
          'icon-allow-overlap': true,
        },
      });

      // Premium flight trail — one selected flight's path (AircraftPanel pushes it)
      map.addSource('aircraft-trail', { type: 'geojson', data: aircraftTrailRef.current ?? EMPTY_FC });
      map.addLayer({
        id: 'aircraft-trail-line',
        type: 'line',
        source: 'aircraft-trail',
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': '#0ea5e9', 'line-width': 3, 'line-opacity': 0.85 },
      }, 'aircraft-markers'); // draw beneath the plane icons

      // Liveuamap incidents — fuchsia so they can't be confused with the
      // red-toned Riskmize incident markers when both layers are on.
      map.addSource('liveuamap', { type: 'geojson', data: liveuamapDataRef.current ?? EMPTY_FC });
      map.addLayer({
        id: 'liveuamap-markers',
        type: 'circle',
        source: 'liveuamap',
        layout: { visibility: liveuamapVisibleRef.current ? 'visible' : 'none' },
        paint: {
          'circle-radius': 6,
          'circle-color': '#d946ef',
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff',
        },
      });

      // ─── Staff phones layers ───────────────────────────────────────────
      // Phase 3 polish unified clustering: staff features now live in the
      // same `vehicles` source so the cluster bubble shows a combined count
      // and is colored by composition. Staff-specific layers filter the
      // shared source by kind=staff.
      if (!map.hasImage('person-glyph')) {
        const personSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="#ffffff" stroke="#1d4ed8" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="8" r="4"/><path d="M4 21v-2a8 8 0 0116 0v2"/></svg>`;
        const personImg = new Image(32, 32);
        personImg.onload = () => {
          if (!map.hasImage('person-glyph')) {
            map.addImage('person-glyph', personImg);
          }
        };
        personImg.src = 'data:image/svg+xml;base64,' + btoa(personSvg);
      }
      // Red variant for a person with an open SOS (mirrors Command's
      // StaffMarker, which fills the marker red while the SOS is active).
      if (!map.hasImage('person-glyph-sos')) {
        const sosSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="#fee2e2" stroke="#dc2626" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="8" r="4"/><path d="M4 21v-2a8 8 0 0116 0v2"/></svg>`;
        const sosImg = new Image(32, 32);
        sosImg.onload = () => {
          if (!map.hasImage('person-glyph-sos')) {
            map.addImage('person-glyph-sos', sosImg);
          }
        };
        sosImg.src = 'data:image/svg+xml;base64,' + btoa(sosSvg);
      }

      // SOS halo behind a person with an open (unacknowledged) SOS — bold red
      // and pulsed fast by the rAF loop below until the alert is acknowledged
      // and the roster poll drops `sos` back to null (MyCRM 233). Added before
      // the selection halo + markers so it draws beneath them.
      map.addLayer({
        id: 'staff-sos-halo',
        type: 'circle',
        source: 'vehicles',
        filter: STAFF_SOS_HALO_FILTER,
        paint: {
          'circle-radius': 30,
          'circle-color': '#ef4444',
          'circle-opacity': 0.3,
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ef4444',
          'circle-stroke-opacity': 0.6,
          'circle-blur': 0.5,
        },
      });

      // Selection halo behind the staff marker — wider + softer than the
      // vehicle halo so it visually wraps the name label below the icon.
      map.addLayer({
        id: 'staff-selection-halo',
        type: 'circle',
        source: 'vehicles',
        filter: ['all',
          ['!', ['has', 'point_count']],
          ['==', ['get', 'kind'], 'staff'],
          ['==', ['get', 'user_id'], '__none__'],
        ],
        paint: {
          'circle-radius': 38,
          'circle-color': '#3b82f6',
          'circle-opacity': 0.14,
          'circle-stroke-width': 2,
          'circle-stroke-color': '#3b82f6',
          'circle-stroke-opacity': 0.45,
          'circle-blur': 0.8,
        },
      });

      map.addLayer({
        id: 'staff-markers',
        type: 'symbol',
        source: 'vehicles',
        filter: ['all',
          ['!', ['has', 'point_count']],
          ['==', ['get', 'kind'], 'staff'],
        ],
        layout: {
          'icon-image': ['case', ['==', ['get', 'sosActive'], true], 'person-glyph-sos', 'person-glyph'],
          'icon-size': 0.9,
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          visibility: 'visible',
        },
      });

      map.addLayer({
        id: 'staff-labels',
        type: 'symbol',
        source: 'vehicles',
        filter: ['all',
          ['!', ['has', 'point_count']],
          ['==', ['get', 'kind'], 'staff'],
        ],
        layout: {
          'text-field': ['get', 'name'],
          'text-size': 11,
          'text-font': ['Noto Sans Bold'],
          'text-offset': [0, 1.6],
          'text-anchor': 'top',
          'text-allow-overlap': false,
          visibility: 'visible',
        },
        paint: {
          'text-color': ['case', ['==', ['get', 'sosActive'], true], '#b91c1c', '#1d4ed8'],
          'text-halo-color': '#ffffff',
          'text-halo-width': 1.5,
        },
        minzoom: 12,
      });
      // Sources exist only from this point (and are wiped by every setStyle)
      // — the POI data effect's whenStyleReady callback can fire on a
      // styledata BEFORE 'load', bail on the missing source, and never retry
      // (poiList is static, unlike the churning vehicle feed). Populate now,
      // and re-apply the POI visibility the layers were just created without
      // (they hardcode visibility:'none' above, ignoring the current toggle).
      // Cluster fan-out (spec 2026-10-30 §3.4) — added last so legs, markers and
      // labels draw above every vehicle, staff and POI layer. Empty until a
      // cluster is fanned; the spacing from clusterFan.js keeps leaves apart, so
      // overlap is allowed. Leaves keep their source properties (`leafKind` is the
      // original 'vehicle' | 'staff'), so the vehicle icon and badge images exist.
      map.addSource('fan', { type: 'geojson', data: EMPTY_FC });
      map.addLayer({
        id: 'fan-legs',
        type: 'line',
        source: 'fan',
        filter: ['==', ['get', 'kind'], 'fan-leg'],
        paint: { 'line-color': '#64748b', 'line-width': 1.5, 'line-opacity': 0.7 },
      });
      const fanLeaf = ['==', ['get', 'kind'], 'fan-leaf'];
      // The selected vehicle's leaf (Group mode keeps its fan open) — same look
      // as vehicle-selection-halo; the selection effect drives the filter.
      map.addLayer({
        id: 'fan-selected-halo',
        type: 'circle',
        source: 'fan',
        filter: fanSelectedHaloFilter(selectedIdRef.current),
        paint: {
          'circle-radius': 32,
          'circle-color': '#3b82f6',
          'circle-opacity': 0.14,
          'circle-stroke-width': 2,
          'circle-stroke-color': '#3b82f6',
          'circle-stroke-opacity': 0.45,
          'circle-blur': 0.7,
        },
      });
      const isStaffLeaf = ['==', ['get', 'leafKind'], 'staff'];
      map.addLayer({
        id: 'fan-markers',
        type: 'symbol',
        source: 'fan',
        filter: fanLeaf,
        layout: {
          'icon-image': ['case', isStaffLeaf,
            ['case', ['==', ['get', 'sosActive'], true], 'person-glyph-sos', 'person-glyph'],
            ['get', 'iconImage']],
          'icon-size': VEHICLE_ICON_SIZE,
          'icon-rotate': ['case', ['==', ['get', 'shouldRotate'], 1], ['get', 'course'], 0],
          'icon-rotation-alignment': 'map',
          'icon-allow-overlap': true,
        },
      });
      // The white upright icon of the "ring" marker style (as vehicle-ring-icons).
      map.addLayer({
        id: 'fan-ring-icons',
        type: 'symbol',
        source: 'fan',
        filter: ['all', fanLeaf, ['has', 'ringIconImage'], ['!=', ['get', 'ringIconImage'], '']],
        layout: {
          'icon-image': ['get', 'ringIconImage'],
          'icon-size': VEHICLE_ICON_SIZE,
          'icon-rotation-alignment': 'viewport',
          'icon-allow-overlap': true,
        },
      });
      // The leaf's badge (same image expression as badgeLayer). The text size
      // ignores the style's label minimum zoom: a fan exists to show its labels.
      // Labels radiate outward (`fanAnchor` from clusterFan.js), so the left and
      // right leaves' wide labels no longer collide under the cluster.
      // Plain labels (shape 'none') and staff leaves resolve to a missing badge
      // image (transparent placeholder) and get a white halo instead.
      const isPlainLeaf = ['==', ['coalesce', ['get', 'labelShape'], 'none'], 'none'];
      map.addLayer({
        id: 'fan-labels',
        type: 'symbol',
        source: 'fan',
        filter: fanLeaf,
        layout: {
          'text-field': ['coalesce', ['get', 'labelText'], ['get', 'name'], ''],
          'text-size': ['number', ['get', 'labelTextSize'], 11],
          'text-font': ['Noto Sans Bold'],
          'text-max-width': BADGE_TEXT_MAX_WIDTH,
          'text-justify': 'auto',
          'text-anchor': ['coalesce', ['get', 'fanAnchor'], 'top'],
          'text-radial-offset': 1.2,
          'icon-anchor': 'top',
          'text-line-height': 1.25,
          'text-allow-overlap': true,
          'icon-image': ['concat', 'label-badge-', ['coalesce', ['get', 'labelShape'], 'none'], '-',
            ['slice', ['downcase', ['coalesce', ['get', 'labelBg'], '#ffffff']], 1]],
          'icon-text-fit': 'both',
          'icon-text-fit-padding': BADGE_PADDING,
          'icon-allow-overlap': true,
        },
        paint: {
          'text-color': ['coalesce', ['get', 'labelFg'], ['case', isStaffLeaf, '#1d4ed8', '#334155']],
          'text-halo-color': '#ffffff',
          'text-halo-width': ['case', isPlainLeaf, 1.5, 0],
          'icon-opacity': ['number', ['get', 'labelOpacity'], 0.9],
        },
      });
      // "+N more" on the faded cluster when it holds more than FAN_MAX leaves
      // (that cluster's own count is filtered out of cluster-count meanwhile).
      map.addLayer({
        id: 'fan-center',
        type: 'symbol',
        source: 'fan',
        filter: ['==', ['get', 'kind'], 'fan-center'],
        layout: {
          'text-field': ['get', 'extraLabel'],
          'text-size': 12,
          'text-font': ['Noto Sans Bold'],
          'text-allow-overlap': true,
          'text-ignore-placement': true,
        },
        paint: { 'text-color': '#0f172a', 'text-halo-color': '#ffffff', 'text-halo-width': 1.5 },
      });

      updatePoiSourcesRef.current?.();
      applyPoiVisibilityRef.current?.();
    }; // end initializeCustomLayers

    map.on('load', initializeCustomLayers);

    // Layer click → container CustomEvent (Riskmize overlay pattern). The
    // overlay components own the popups; MapView only forwards the hit.
    // Registered ONCE here (not inside initializeCustomLayers) — delegated
    // per-layer handlers survive a layer being removed/re-added on a basemap
    // switch, so registering inside the rebuild function would duplicate the
    // handler on every switch.
    const forwardLayerClick = (eventName) => (e) => {
      const f = e.features?.[0];
      if (!f) return;
      map.getContainer().dispatchEvent(new CustomEvent(eventName, {
        detail: { properties: { ...f.properties }, lngLat: [e.lngLat.lng, e.lngLat.lat], anchor: { x: e.point.x, y: e.point.y } },
      }));
    };
    map.on('click', 'aircraft-markers', forwardLayerClick('trackizy:aircraft-click'));
    map.on('click', 'liveuamap-markers', forwardLayerClick('trackizy:liveuamap-click'));

    // Cluster fan-out (spec 2026-10-30 §3.4). Every handler is registered ONCE
    // (delegated layer handlers survive the layer rebuild on a basemap switch).
    const queryLayers = (point, ids) => {
      const layers = ids.filter((id) => map.getLayer(id));
      return layers.length ? map.queryRenderedFeatures(point, { layers }) : [];
    };
    const leafKey = (leaves) => leaves
      .map(({ properties: p = {} }) => (p.kind === 'staff' ? `s${p.user_id}` : `v${p.id}`))
      .sort()
      .join(',');
    let fanCheckPending = false; // new vehicle data indexed; recheck once tiles load
    let fanMisses = 0; // consecutive ticks the fanned leaf set was not found
    const closeFan = () => {
      if (!fanStateRef.current) return;
      fanStateRef.current = null;
      fanSeqRef.current += 1;
      map.getSource('fan')?.setData(EMPTY_FC);
      if (map.getLayer('clusters')) map.setPaintProperty('clusters', 'circle-opacity', CLUSTER_OPACITY);
      if (map.getLayer('cluster-count')) map.setFilter('cluster-count', CLUSTER_COUNT_FILTER);
    };
    closeFanRef.current = closeFan;
    // The fanned cluster's circle fades to 0.3; with "+N more" on top, its own
    // count is filtered out of cluster-count.
    const styleFannedCluster = (clusterId, extra) => {
      if (map.getLayer('clusters')) {
        map.setPaintProperty('clusters', 'circle-opacity',
          ['case', ['==', ['get', 'cluster_id'], clusterId], 0.3, CLUSTER_OPACITY]);
      }
      if (map.getLayer('cluster-count')) {
        map.setFilter('cluster-count', extra > 0
          ? ['all', CLUSTER_COUNT_FILTER, ['!=', ['get', 'cluster_id'], clusterId]]
          : CLUSTER_COUNT_FILTER);
      }
    };
    // `key` is the leaf key of the cluster's first FAN_MAX leaves (what
    // recheckFan compares against); it differs from `leafKey(leaves)` only when a
    // selected vehicle beyond the first FAN_MAX was swapped into the shown set.
    const openFan = (clusterId, center, leaves, total, { key = leafKey(leaves) } = {}) => {
      const source = map.getSource('fan');
      if (!source) return;
      const { fc, extra } = buildFanFC(
        center, leaves, fanOffsets(leaves.length),
        (lngLat) => map.project(lngLat),
        ({ x, y }) => map.unproject([x, y]).toArray(),
        { total, extraLabel: (count) => tRef.current('mapLabels.crowding.fanMore', { count }) },
      );
      source.setData(fc);
      styleFannedCluster(clusterId, extra);
      fanSeqRef.current += 1;
      fanMisses = 0;
      fanStateRef.current = {
        clusterId, center, extra, leafKey: key, zoom: map.getZoom(),
        shown: new Set(leafKey(leaves).split(',')),
        leafFeatures: fc.features.filter((f) => f.properties.kind === 'fan-leaf'),
      };
    };

    // Cluster click: zoom in while that still splits it, otherwise fan it out.
    // Clicking the open fan's own cluster closes it.
    map.on('click', 'clusters', (e) => {
      if (queryLayers(e.point, FAN_HIT_LAYERS).length) return; // a leaf drawn over the faded circle
      const feature = map.queryRenderedFeatures(e.point, { layers: ['clusters'] })[0];
      if (!feature) return;
      const clusterId = feature.properties.cluster_id;
      if (fanStateRef.current?.clusterId === clusterId) { closeFan(); return; }
      const center = feature.geometry.coordinates;
      const source = map.getSource('vehicles');
      const seq = fanSeqRef.current;
      Promise.all([source.getClusterExpansionZoom(clusterId), source.getClusterLeaves(clusterId, FAN_MAX, 0)])
        .then(([expansionZoom, leaves]) => {
          if (seq !== fanSeqRef.current) return;
          const decision = fanClickDecision({
            currentZoom: map.getZoom(), expansionZoom, maxZoom: FAN_ZOOM_CEILING, leavesCoLocated: leavesCoLocated(leaves),
          });
          if (decision === 'zoom') { map.easeTo({ center, zoom: expansionZoom }); return; }
          closeFan();
          openFan(clusterId, center, leaves, feature.properties.point_count ?? leaves.length);
        })
        .catch(() => {});
    });
    map.on('mouseenter', 'clusters', () => { map.getCanvas().style.cursor = 'pointer'; });
    map.on('mouseleave', 'clusters', () => { map.getCanvas().style.cursor = ''; });

    // A click on a fanned leaf selects it exactly as the marker layers do; any
    // other click (except on a cluster, handled above) closes the fan.
    // Leaf icons and labels overlap, so the hit is the leaf icon whose point is
    // nearest the click — never a label, never simply the first feature.
    const projectPt = (lngLat) => map.project(lngLat);
    map.on('click', (e) => {
      if (!fanStateRef.current) return;
      let hits = queryLayers(e.point, ['fan-markers']);
      if (!hits.length) hits = queryLayers(e.point, ['fan-ring-icons']);
      const leaf = nearestFanLeaf(hits, e.point, projectPt);
      if (leaf) {
        const p = leaf.properties;
        if (p.leafKind === 'staff') {
          closeFan();
          dispatch(devicesActions.selectId(null));
          if (p.user_id) dispatch(staffActions.selectUser(p.user_id));
          return;
        }
        // Group mode keeps the fan open: the vehicle stays clustered at every
        // zoom, so the fan is the only place its marker and label show.
        if (crowdingRef.current !== 'group') closeFan();
        dispatch(staffActions.clearSelection());
        dispatch(devicesActions.selectId(p.id));
        return;
      }
      // A click on a label (or just beside a leaf) does nothing — unless the org
      // turned on "clicking a label selects the vehicle".
      const labelHit = queryLayers(e.point, ['fan-labels'])[0];
      if (labelHit && labelClickSelectsRef.current && labelHit.properties.leafKind === 'vehicle') {
        if (crowdingRef.current !== 'group') closeFan();
        dispatch(staffActions.clearSelection());
        dispatch(devicesActions.selectId(labelHit.properties.id));
        return;
      }
      if (nearestFanLeaf(fanStateRef.current.leafFeatures, e.point, projectPt, FAN_NEAR_LEAF_PX)) return;
      if (labelHit) return; // a label click is never an empty click
      if (queryLayers(e.point, ['clusters']).length) return;
      closeFan();
    });
    for (const id of FAN_LEAF_LAYERS) {
      map.on('mouseenter', id, () => { map.getCanvas().style.cursor = 'pointer'; });
      map.on('mouseleave', id, () => { map.getCanvas().style.cursor = ''; });
    }
    // Close on a manual zoom, and on a pan that moved the cluster more than
    // 50 px. The selection camera (fanCameraMoveRef) is exempt: its fan is
    // re-checked on that move's moveend (fanForSelection).
    map.on('zoomstart', () => { if (!fanCameraMoveRef.current) closeFan(); });
    map.on('movestart', () => {
      const fan = fanStateRef.current;
      fanMoveStartPxRef.current = fan && !fanCameraMoveRef.current ? map.project(fan.center) : null;
    });
    map.on('moveend', () => {
      if (fanCameraMoveRef.current) { fanCameraMoveRef.current = false; fanMoveStartPxRef.current = null; return; }
      const fan = fanStateRef.current;
      const start = fanMoveStartPxRef.current;
      fanMoveStartPxRef.current = null;
      if (fan && start) {
        const now = map.project(fan.center);
        if (Math.hypot(now.x - start.x, now.y - start.y) > FAN_CLOSE_DRAG_PX) closeFan();
      }
      // A user zoom/pan (zoomend is followed by moveend) re-fans the selection
      // without waiting for its next fix.
      refanSelectionWhenLoaded();
    });
    // Close when a positions tick re-clustered the fanned vehicles. Supercluster
    // renumbers clusters whenever clustering changes ANYWHERE at that zoom (the
    // id is an index into the level's array), so the fan is matched by its leaf
    // set, not its id: after the worker indexed new data ('metadata') and the
    // tiles reloaded, ask the clusters now drawn at the fan centre for their
    // leaves. A match keeps the fan (re-pointing the fade to the new id); two
    // misses in a row close it — one miss can be a tick that landed between the
    // rendered tiles and the worker index.
    const recheckFan = () => {
      const fan = fanStateRef.current;
      if (!fan) return;
      const seq = fanSeqRef.current;
      const c = map.project(fan.center);
      const r = FAN_RECHECK_BOX_PX;
      const ids = [...new Set(queryLayers([[c.x - r, c.y - r], [c.x + r, c.y + r]], ['clusters'])
        .map((f) => f.properties.cluster_id))];
      const source = map.getSource('vehicles');
      Promise.all(ids.map((id) => source.getClusterLeaves(id, FAN_MAX, 0)
        .then((leaves) => (leafKey(leaves) === fan.leafKey ? id : null))
        .catch(() => null)))
        .then((found) => {
          if (seq !== fanSeqRef.current) return;
          const id = found.find((x) => x != null);
          if (id == null) {
            fanMisses += 1;
            if (fanMisses >= 2) { closeFan(); refanSelection(); }
            return;
          }
          fanMisses = 0;
          if (id !== fan.clusterId) {
            fan.clusterId = id;
            styleFannedCluster(id, fan.extra);
          }
        });
    };
    // Group mode: fan the cluster hiding the selected vehicle so its marker,
    // halo and label stay visible. Re-opens only when the selected id, the
    // containing cluster's leaf set or the zoom changed (Follow re-flies every
    // tick); does nothing when the vehicle is not clustered at this zoom.
    const fanForSelection = (id, lngLat) => {
      if (crowdingRef.current !== 'group' || id == null || !lngLat) return;
      const source = map.getSource('vehicles');
      if (!source || !map.getLayer('clusters')) return;
      const p = map.project(lngLat);
      const r = FAN_SELECTION_BOX_PX;
      const clusters = queryLayers([[p.x - r, p.y - r], [p.x + r, p.y + r]], ['clusters']);
      const isSelected = (leaf) => leaf.properties?.kind === 'vehicle' && String(leaf.properties.id) === String(id);
      const seq = fanSeqRef.current;
      const zoom = map.getZoom();
      Promise.all(clusters.map((f) => source
        .getClusterLeaves(f.properties.cluster_id, f.properties.point_count ?? FAN_MAX, 0)
        .then((leaves) => (leaves.some(isSelected) ? { feature: f, leaves } : null))
        .catch(() => null)))
        .then((found) => {
          if (seq !== fanSeqRef.current || map.getZoom() !== zoom) return; // superseded
          const hit = found.find(Boolean);
          const fan = fanStateRef.current;
          const sameZoom = fan && Math.abs(fan.zoom - zoom) < 0.01;
          if (!hit) {
            // A fan laid out at another zoom has the wrong geometry now.
            if (fan && !sameZoom) closeFan();
            return;
          }
          const first = hit.leaves.slice(0, FAN_MAX);
          const key = leafKey(first);
          const clusterId = hit.feature.properties.cluster_id;
          if (fan && sameZoom && fan.leafKey === key && fan.shown.has(`v${id}`)) {
            if (fan.clusterId !== clusterId) { fan.clusterId = clusterId; styleFannedCluster(clusterId, fan.extra); }
            return;
          }
          // Keep the cluster's own leaf order (a clicked leaf does not jump);
          // swap the selected one in when it is beyond the first FAN_MAX.
          const shown = first.some(isSelected) ? first : [...first.slice(0, FAN_MAX - 1), hit.leaves.find(isSelected)];
          closeFan();
          openFan(clusterId, hit.feature.geometry.coordinates, shown,
            hit.feature.properties.point_count ?? hit.leaves.length, { key });
        });
    };
    fanForSelectionRef.current = fanForSelection;
    // Group mode with a selection: fan the cluster now hiding it. Event-driven
    // (user move, recheck close, crowding → group), never per positions message.
    const refanSelection = () => {
      const id = selectedIdRef.current;
      if (crowdingRef.current !== 'group' || id == null) return;
      const p = positionsRef.current?.[id];
      if (p && p.latitude !== 0) fanForSelection(id, [p.longitude, p.latitude]);
    };
    // After a move the rendered clusters may still be the previous zoom's tiles.
    const refanSelectionWhenLoaded = () => {
      if (crowdingRef.current !== 'group' || selectedIdRef.current == null) return;
      if (map.isSourceLoaded('vehicles')) refanSelection();
      else map.once('idle', refanSelection);
    };
    refanSelectionRef.current = () => map.once('idle', refanSelection);
    map.on('sourcedata', (e) => {
      if (e.sourceId !== 'vehicles' || !fanStateRef.current) return;
      if (e.sourceDataType === 'metadata') { fanCheckPending = true; return; }
      if (!fanCheckPending || !map.isSourceLoaded('vehicles')) return;
      fanCheckPending = false;
      recheckFan();
    });

    // Label cards are rendered on demand: MapLibre asks for an image only when it
    // places a card, so off-screen cards cost nothing. Registered ONCE (map events
    // survive setStyle; after a basemap switch the images are re-requested here).
    map.on('styleimagemissing', ({ id }) => {
      if (typeof id !== 'string' || !id.startsWith('card-')) return;
      const base = getCardSpec(baseCardId(id));
      if (!base || map.hasImage(id)) return;
      // The selected twin is derived from its base id; eviction removes both.
      const spec = id.startsWith('card-sel-') ? { ...base, selected: true } : base;
      const rendered = renderCardImage(spec);
      if (rendered) map.addImage(id, rendered.image, rendered.options);
    });

    // Re-initialize custom layers after style change (setStyle removes all layers)
    let initialLoadDone = false;
    map.on('style.load', () => {
      if (!initialLoadDone) {
        initialLoadDone = true;
        return; // skip first style.load — handled by 'load' event
      }
      initializeCustomLayers();
    });

    mapRef.current = map;
    tryFleetFitRef.current?.();

    // Suspect-halo "breathe" — a single, self-terminating rAF loop registered
    // ONCE here (never inside initializeCustomLayers) so a basemap switch, which
    // removes+re-adds `vehicle-suspect-halo`, can't stack a second loop. The
    // loop keeps ticking across style rebuilds: while the layer is transiently
    // absent mid-switch it just skips that frame's paint, and it self-terminates
    // only when the map itself is gone (leak backstop). Mirrors the flow-dash
    // pattern in src/lib/replayFlowDash.js. Gentle + subtle by design (radius
    // 22->32, fill opacity 0.05->0.16, ~2.4s period) — softer than SOS/selection.
    // The SOS halos (MyCRM 233) ride the same loop with a faster, bolder
    // beat (~1.2s period, radius 24->38 / 34->48, opacity 0.15->0.45) — an
    // emergency should be impossible to miss, unlike the suspect hint. Users
    // who asked the OS for reduced motion get a steady halo instead.
    const reduceMotion =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const animateSuspectPulse = (ts) => {
      const m = mapRef.current;
      if (!m || m._removed) { suspectPulseRef.current = null; return; }
      if (suspectPresentRef.current && m.getLayer('vehicle-suspect-halo')) {
        const phase = (Math.sin((ts / 2400) * Math.PI * 2) + 1) / 2; // 0..1
        m.setPaintProperty('vehicle-suspect-halo', 'circle-radius', 22 + phase * 10);
        m.setPaintProperty('vehicle-suspect-halo', 'circle-opacity', 0.05 + phase * 0.11);
      }
      if (sosPresentRef.current && !reduceMotion) {
        const beat = (Math.sin((ts / 1200) * Math.PI * 2) + 1) / 2; // 0..1
        if (m.getLayer('staff-sos-halo')) {
          m.setPaintProperty('staff-sos-halo', 'circle-radius', 24 + beat * 14);
          m.setPaintProperty('staff-sos-halo', 'circle-opacity', 0.15 + beat * 0.3);
        }
        if (m.getLayer('cluster-sos-halo')) {
          m.setPaintProperty('cluster-sos-halo', 'circle-radius', 34 + beat * 14);
          m.setPaintProperty('cluster-sos-halo', 'circle-opacity', 0.15 + beat * 0.3);
        }
      }
      suspectPulseRef.current = requestAnimationFrame(animateSuspectPulse);
    };
    suspectPulseRef.current = requestAnimationFrame(animateSuspectPulse);

    return () => {
      if (routeAnimationRef.current) {
        cancelAnimationFrame(routeAnimationRef.current);
        routeAnimationRef.current = null;
      }
      if (suspectPulseRef.current) {
        cancelAnimationFrame(suspectPulseRef.current);
        suspectPulseRef.current = null;
      }
      map.remove();
      mapRef.current = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Update vehicle positions + load custom icons on data change
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const update = () => {
      const source = map.getSource('vehicles');
      if (!source) return;

      // Pre-generate predefined marker images (colored + ring styles)
      ensureMarkerImages(map, devices, loadedIcons.current);

      // Load custom device icons that haven't been loaded yet
      for (const device of Object.values(devices)) {
        const { style, icon, customUrl } = resolveMarkerConfig(device.attributes);
        const key = `device-${device.id}`;
        if (icon === 'custom' && customUrl && !loadedIcons.current.has(key)) {
          loadedIcons.current.add(key);
          loadScaledIcon(customUrl)
            .then((scaled) => {
              if (!map.hasImage(key)) {
                map.addImage(key, scaled);
                // Re-set data so the icon shows up
                source.setData(buildCombinedAssetFC(devices, effectivePositions, thresholds, staffByUserId, vehiclesEnabled, vehicleStatuses, labelCtx));
              }
            })
            .catch(() => {
              // Fallback: remove from loaded so arrow is used
              loadedIcons.current.delete(key);
            });
        }
      }

      const fc = buildCombinedAssetFC(devices, effectivePositions, thresholds, staffByUserId, vehiclesEnabled, vehicleStatuses, labelCtx);
      ensureBadgeImages(map, fc.features);
      // The per-feature minimum zoom hides labels via expressions; the LAYER
      // floor follows the lowest value in use so nothing is laid out further out
      // than any style asks for (default fleets stay at 12, as before).
      const floor = lowestLabelMinZoom(fc.features);
      if (floor !== labelFloorRef.current) {
        for (const id of LABEL_LAYER_IDS) if (map.getLayer(id)) map.setLayerZoomRange(id, floor, 24);
        labelFloorRef.current = floor;
      }
      // Gate the always-on suspect-pulse rAF: only repaint the halo while a
      // suspect vehicle is actually on screen.
      suspectPresentRef.current = fc.features.some((f) => f.properties?.suspect === 1);
      // Clusters are derived from these same features, so one SOS person on
      // the map is enough to keep both SOS halo layers pulsing.
      sosPresentRef.current = fc.features.some((f) => f.properties?.sosActive === true);
      // Per-user "Cluster vehicles" pref: re-tile before pushing data.
      // Only on a real change: each call queues a full re-index.
      const applied = clusterAppliedRef.current;
      if (layerPrefs.cluster !== applied.cluster || labelCtx.crowding !== applied.crowding) {
        source.setClusterOptions?.(clusterOptionsFor({ clusterEnabled: layerPrefs.cluster, crowding: labelCtx.crowding }));
        clusterAppliedRef.current = { cluster: layerPrefs.cluster, crowding: labelCtx.crowding };
      }
      source.setData(fc);
      // Forget card images no feature uses any more (a label's text changes with
      // its speed, so every tick can mint new cards).
      const liveCards = new Set();
      for (const f of fc.features) {
        const card = f.properties?.labelCard;
        if (card) { liveCards.add(card); liveCards.add(`card-sel-${card.slice(5)}`); }
      }
      evictUnusedCards(map, liveCards);
    };

    const detachStyleReady = whenStyleReady(map, update);
    return () => detachStyleReady();
  }, [devices, effectivePositions, thresholds, staffByUserId, vehiclesEnabled, vehicleStatuses, labelCtx, layerPrefs.cluster]);

  // Update POI layer data
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const update = () => {
      const pointSource = map.getSource('pois');
      const circleSource = map.getSource('poi-circles');
      if (!pointSource || !circleSource) return;

      const pointFeatures = [];
      const circleFeatures = [];

      poiList.forEach((poi) => {
        if (!poi.latitude || !poi.longitude) return;

        const color = poi.color || poi.poi_categories?.color || '#3b82f6';
        const icon = poi.icon || poi.poi_categories?.icon || 'map-pin';
        const radius = poi.radius_meters || 100;

        pointFeatures.push({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [poi.longitude, poi.latitude] },
          properties: {
            id: poi.id,
            name: poi.name,
            color,
            icon,
            radius,
            isGlobal: poi.organization_id === null,
            category_slug: poi.poi_categories?.slug || poi.category_slug || '',
          },
        });

        circleFeatures.push({
          type: 'Feature',
          geometry: { type: 'Polygon', coordinates: [circleToPolygon(poi.latitude, poi.longitude, radius)] },
          properties: { id: poi.id, color },
        });
      });

      pointSource.setData({ type: 'FeatureCollection', features: pointFeatures });
      circleSource.setData({ type: 'FeatureCollection', features: circleFeatures });
    };

    updatePoiSourcesRef.current = update;
    const detachStyleReady = whenStyleReady(map, update);
    return () => detachStyleReady();
  }, [poiList]);

  // Toggle POI layer visibility. isStyleLoaded() is false while the style OR
  // its tiles are still loading (satellite basemaps take seconds) — a bare
  // bail here silently ate the user's toggle click; whenStyleReady defers
  // the flip until the style settles instead.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const apply = () => {
      const vis = showPOIs ? 'visible' : 'none';
      ['poi-circle-fill', 'poi-circle-border', 'poi-markers', 'poi-icon-symbols', 'poi-labels', 'assembly-points-layer'].forEach((id) => {
        if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', vis);
      });
    };
    applyPoiVisibilityRef.current = apply;
    const detachStyleReady = whenStyleReady(map, apply);
    return () => detachStyleReady();
  }, [showPOIs]);

  // Traffic tiles visibility — the layer itself is (re)created by
  // initializeCustomLayers; this effect only flips visibility. Gated on
  // features.trafficLayer so a stale localStorage toggle can't show traffic
  // (and fetch Mapbox tiles) in orgs where the flag is off — there's no UI
  // to disable it once org-level access is revoked otherwise.
  useEffect(() => {
    const trafficActive = layerPrefs.traffic && features.trafficLayer;
    trafficVisibleRef.current = trafficActive;
    const map = mapRef.current;
    if (!map) return undefined;
    return whenStyleReady(map, () => {
      if (map.getLayer('traffic-lines')) {
        map.setLayoutProperty('traffic-lines', 'visibility', trafficActive ? 'visible' : 'none');
      }
    });
  }, [layerPrefs.traffic, features.trafficLayer]);

  // Weather tile layer — raster sources can't retarget their tile URL
  // template after creation, so switching between rain/clouds/temp/wind (or
  // turning the layer off) removes and re-adds both the source and the
  // layer. weatherTileLayerRef is updated FIRST so a basemap switch (which
  // rebuilds every custom layer via initializeCustomLayers) reseeds whatever
  // layer was last selected instead of resetting to off. Driven by
  // weatherActive (availability-gated), NOT the raw pref — so availability
  // flipping off tears the layer down, and flipping on rebuilds the pref.
  useEffect(() => {
    weatherTileLayerRef.current = weatherActive;
    const map = mapRef.current;
    if (!map) return undefined;
    return whenStyleReady(map, () => {
      if (map.getLayer('weather-tiles-layer')) map.removeLayer('weather-tiles-layer');
      if (map.getSource('weather-tiles')) map.removeSource('weather-tiles');
      if (weatherActive !== 'off') {
        map.addSource('weather-tiles', {
          type: 'raster',
          tiles: [`${window.location.origin}/api/layers/weather/tile/${weatherActive}/{z}/{x}/{y}`],
          tileSize: 256,
          maxzoom: 12,
        });
        map.addLayer({
          id: 'weather-tiles-layer', type: 'raster', source: 'weather-tiles',
          paint: { 'raster-opacity': 0.65 },
        });
        map.moveLayer('weather-tiles-layer', map.getLayer('traffic-lines') ? 'traffic-lines' : 'clusters');
      }
    });
  }, [weatherActive]);

  // First view = the fleet, not the theme's country-level default. The
  // zoom-6 start pulled a dozen 75–150 KB vector tiles for all of Iraq and
  // then the user scrolled in through four more tile batches before seeing
  // their vehicles (Ms. Mayan's GPSwox comparison, 2026-10-26). Once, when
  // positions first arrive, fit the camera to every usable fix — but ONLY if
  // the camera is still parked on the theme default: an alert deep link
  // (livemap-fly-to), a saved view or the user's own drag/zoom may already
  // have moved it, and those must win. Instant (duration 0) so the only tile
  // batch fetched is the one the user actually looks at.
  // Runs from two places: the positions effect below (positions arrive after
  // the map, the cold /map load) AND the end of the map-init effect (positions
  // were already in Redux when the user navigated here from another page —
  // an idle fleet may not emit a single fix for minutes, so waiting for the
  // next positions change would never fit; caught on the staging test fleet).
  const fleetFitDoneRef = useRef(false);
  const positionsRef = useRef(positions);
  positionsRef.current = positions;
  const tryFleetFit = useCallback(() => {
    const map = mapRef.current;
    if (!map || fleetFitDoneRef.current) return;
    const bounds = fleetBounds(positionsRef.current);
    if (!bounds) return;
    fleetFitDoneRef.current = true;
    const c = map.getCenter();
    const untouched =
      Math.abs(map.getZoom() - zoom) < 1e-6 &&
      Math.abs(c.lng - center[0]) < 1e-6 &&
      Math.abs(c.lat - center[1]) < 1e-6;
    if (!untouched) return;
    map.fitBounds(bounds, { padding: 120, maxZoom: 15, duration: 0 });
  }, [center, zoom]);
  tryFleetFitRef.current = tryFleetFit;
  useEffect(() => { tryFleetFit(); }, [positions, tryFleetFit]);

  // A selection change closes the cluster fan, unless the newly selected vehicle
  // is one of its shown leaves (Group mode: a leaf click keeps the fan open). In
  // Group mode the selection effect then fans the cluster hiding the vehicle.
  // Declared BEFORE that effect so its close does not supersede the new fan.
  useEffect(() => {
    const fan = fanStateRef.current;
    if (fan && selectedId != null && crowdingRef.current === 'group' && fan.shown?.has(`v${selectedId}`)) return;
    closeFanRef.current?.();
  }, [selectedId]);

  // Crowding resolved to Group with a vehicle selected: fan its cluster once the
  // re-clustered tiles have rendered (the source options change in the data effect).
  useEffect(() => {
    if (crowding === 'group' && selectedId != null) refanSelectionRef.current?.();
    // Only the crowding change triggers this; selection has its own effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crowding]);

  // Fly to selected vehicle + drive the selection halo filter
  const lastFlownVehicleRef = useRef(null);
  const lastFlownStaffRef = useRef(null);
  // Inputs of the last selection camera/fan run (see runKey below).
  const lastSelectionRunRef = useRef(null);
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    // Update halo filter so the glow tracks the selection. Sentinel id -1
    // matches no feature when nothing is selected.
    if (map.getLayer('vehicle-selection-halo')) {
      map.setFilter('vehicle-selection-halo', [
        'all',
        ['!', ['has', 'point_count']],
        ['==', ['get', 'kind'], 'vehicle'],
        ['==', ['get', 'id'], selectedId ?? -1],
      ]);
    }

    for (const glow of GLOW_LAYER_VARIANTS) {
      if (map.getLayer(glow.id)) map.setFilter(glow.id, vehicleBadgeGlowFilter(selectedId, glow.tight));
    }
    // The selected badge moves to the fixed-anchor layer so it sits over its glow.
    for (const v of BADGE_LAYER_VARIANTS) {
      if (map.getLayer(v.id)) map.setFilter(v.id, vehicleBadgeUnselectedFilter(selectedId, v.tight));
    }
    for (const v of SELECTED_BADGE_LAYER_VARIANTS) {
      if (map.getLayer(v.id)) map.setFilter(v.id, vehicleBadgeSelectedFilter(selectedId, v.tight));
    }
    if (map.getLayer(CARD_SELECTED_LAYER_ID)) {
      map.setFilter(CARD_SELECTED_LAYER_ID, ['all', VEHICLE_CARD_FILTER, ['==', ['get', 'id'], selectedId ?? -1]]);
    }
    if (map.getLayer('fan-selected-halo')) map.setFilter('fan-selected-halo', fanSelectedHaloFilter(selectedId));

    // The server sends one message per position row, so `positions` changes many
    // times a second. Only the selected vehicle's OWN fix (or the selection, the
    // Follow toggle, a replay) re-runs the camera and the Group-mode fan check.
    const pos = selectedId != null ? positions[selectedId] : null;
    const runKey = [selectedId ?? '', pos?.latitude, pos?.longitude, pos?.fixTime, followSelection, suppressSelectionFlyTo].join('|');
    if (runKey === lastSelectionRunRef.current) return;
    lastSelectionRunRef.current = runKey;

    // Group mode: fan out the cluster hiding the selected vehicle — on the
    // programmatic move's moveend, or right away when the camera does not move.
    if (fanMoveEndRef.current) { map.off('moveend', fanMoveEndRef.current); fanMoveEndRef.current = null; }
    const fanCheck = () => {
      const p = positionsRef.current?.[selectedId];
      if (p && p.latitude !== 0) fanForSelectionRef.current?.(selectedId, [p.longitude, p.latitude]);
    };

    if (!selectedId) { lastFlownVehicleRef.current = null; return; }
    // While a replay is active, the replay subject IS the selected vehicle.
    // Auto-following its LIVE position on every incoming fix would zoom to 14
    // and re-center off the historical trail that MiniReplayLayer just fit the
    // camera to — ejecting the whole trail from view ("track does not render",
    // most visible for moving vehicles whose live fixes keep arriving). Skip
    // the camera move during replay; the halo filter above still tracks it.
    if (suppressSelectionFlyTo) return;
    const group = crowdingRef.current === 'group';
    // Follow off (per-user toolbar toggle): fly once per selection change, then
    // leave the camera alone as fixes keep arriving.
    if (!selectionCameraMove({ follow: followSelection, selectedId, lastFlownId: lastFlownVehicleRef.current })) {
      if (group) fanCheck();
      return;
    }
    if (pos && pos.latitude !== 0) {
      lastFlownVehicleRef.current = selectedId;
      if (group) {
        // flyTo() calls stop() first, whose moveend would clear the flag set for
        // THIS flight; stop the old one before raising it. The init effect's
        // moveend listener (registered first) clears the flag, then fanCheck runs.
        map.stop();
        fanCameraMoveRef.current = true;
        const onMoveEnd = () => {
          fanMoveEndRef.current = null;
          fanCameraMoveRef.current = false;
          fanCheck();
        };
        fanMoveEndRef.current = onMoveEnd;
        map.once('moveend', onMoveEnd);
      }
      map.flyTo({
        center: [pos.longitude, pos.latitude],
        zoom: Math.max(map.getZoom(), 14),
        duration: 1000,
      });
    }
  }, [selectedId, positions, suppressSelectionFlyTo, followSelection]);

  // Cards baked before DM Sans loaded used the fallback font: drop them once the
  // fonts are ready so MapLibre re-requests and re-renders them.
  useEffect(() => {
    let cancelled = false;
    document.fonts?.ready?.then(() => {
      if (!cancelled && mapRef.current) removeAllCardImages(mapRef.current);
    });
    return () => { cancelled = true; };
  }, []);

  // Fly to selected staff person (Phase 3 polish — mirrors the vehicle path)
  // + drive the staff selection halo filter.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (map.getLayer('staff-selection-halo')) {
      map.setFilter('staff-selection-halo', [
        'all',
        ['!', ['has', 'point_count']],
        ['==', ['get', 'kind'], 'staff'],
        ['==', ['get', 'user_id'], selectedStaffUserId ?? '__none__'],
      ]);
    }

    if (!selectionCameraMove({ follow: followSelection, selectedId: selectedStaffUserId, lastFlownId: lastFlownStaffRef.current })) return;
    const row = staffByUserId[selectedStaffUserId];
    const fix = row?.lastFix;
    if (fix && Number.isFinite(fix.latitude) && Number.isFinite(fix.longitude)) {
      lastFlownStaffRef.current = selectedStaffUserId;
      map.flyTo({
        center: [fix.longitude, fix.latitude],
        zoom: Math.max(map.getZoom(), 14),
        duration: 1000,
      });
    }
  }, [selectedStaffUserId, staffByUserId, followSelection]);

  // Snap overlay for the trail-line source: when snap is on, swap each
  // per-device LineString's coordinates with the OSRM-matched polyline,
  // TRUNCATED at the current marker position. Truncation matters because the
  // animator renders the marker "1 fix behind" real time, so the marker's
  // projection lands somewhere INSIDE the polyline — the part of the
  // polyline past the projection would render as "trail ahead of marker",
  // which operators read as confusing/wrong.
  //
  // Falls through to raw `trailData` when snap is off or no match exists.
  const effectiveTrailData = useMemo(() => {
    if (!snapTrails.enabled || !trailData?.features?.length || snapTrails.polylineById.size === 0) {
      return trailData;
    }
    return {
      type: 'FeatureCollection',
      features: trailData.features.map((feature) => {
        const deviceId = feature?.properties?.deviceId;
        const snapped = snapTrails.polylineById.get(deviceId);
        if (!snapped) return feature;
        // Find the marker's current position. The animator (when on) gives us
        // the displayed point; otherwise read straight from Redux. We then
        // project that onto the snapped polyline and slice everything past
        // the projection off — trail ends exactly at the marker.
        const markerSource =
          (animatePositions && animatedSnapshot.get(deviceId)) || positions[deviceId];
        if (!markerSource || !Number.isFinite(markerSource.latitude) || !Number.isFinite(markerSource.longitude)) {
          return { ...feature, geometry: { type: 'LineString', coordinates: snapped.coordinates } };
        }
        // Phase 3 animation: when mid-tween, truncate at the marker's
        // along-road point (same walk effectivePositions uses) so the trail
        // ends exactly where the marker is, even through a curve.
        let projection = null;
        if (markerSource._animPrev && markerSource._animTarget && typeof markerSource._animFraction === 'number') {
          const along = interpolateAlongPolyline(markerSource._animPrev, markerSource._animTarget, markerSource._animFraction, snapped, snapThresholdMeters);
          if (along) {
            projection = { latitude: along.latitude, longitude: along.longitude, segmentIndex: along.segmentIndex, distanceMeters: 0 };
          }
        }
        if (!projection) {
          projection = projectOnPolyline(markerSource.latitude, markerSource.longitude, snapped);
        }
        // Beyond the projection cap = marker has moved past the polyline's
        // end. The full polyline IS already behind the marker in that case,
        // so don't truncate — just emit the polyline as-is.
        const truncated = projection && projection.distanceMeters <= snapThresholdMeters
          ? truncatePolylineAtProjection(snapped, projection)
          : snapped;
        return {
          ...feature,
          geometry: { type: 'LineString', coordinates: truncated.coordinates },
        };
      }),
    };
  }, [trailData, snapTrails.enabled, snapTrails.polylineById, snapTrails.tick, positions, animatedSnapshot, animatePositions, snapThresholdMeters]);

  // Update trail data — layer is always visible, empty data = nothing renders
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const data = effectiveTrailData || { type: 'FeatureCollection', features: [] };
    const update = () => {
      let source = map.getSource('trails');
      if (!source) {
        // Source missing — recreate it and the layer
        try {
          map.addSource('trails', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
          map.addLayer({
            id: 'trails-line',
            type: 'line',
            source: 'trails',
            minzoom: trailMinZoom,
            layout: { 'line-join': 'round', 'line-cap': 'round' },
            paint: { 'line-width': 4, 'line-color': '#10b981', 'line-opacity': 0.7 },
          });
          source = map.getSource('trails');
        } catch (e) {
          // Source creation can fail if style isn't loaded yet — will retry on next update
          return;
        }
      }
      if (source) {
        source.setData(data);
      }
      // Update minzoom dynamically
      if (map.getLayer('trails-line')) {
        map.setLayerZoomRange('trails-line', trailMinZoom, 24);
      }
    };
    update();
  }, [effectiveTrailData, trailMinZoom]);

  // Riskmize drawer shift: when the drawer is open AND its logical side
  // equals the cluster's logical side (always 'end'), we must slide the
  // cluster inward by drawer width + gap so the controls don't sit behind
  // the drawer. drawerSide = opposite of assetsPanelSide.
  const riskDrawerOpen = useSelector((s) => s.riskmize.drawerOpen);
  const drawerSide = props.panelSide === 'start' ? 'end' : 'start';
  const RISK_DRAWER_SHIFT = 332; // 320 width + 12 gap (see IncidentsDrawer)
  // Cluster is always anchored at inline-end. Shift adds up for anything
  // occupying that side — the vehicles panel when panelSide === 'end',
  // or the Riskmize drawer when it lives on the 'end' side.
  // With the list on the end side, the host's Flip/Trails/Animate/Snap
  // stack sits at panelWidth + 8 on that same edge (LiveMapPage), so the
  // cluster must also clear that stack's width or the two overlap.
  const PANEL_EDGE_STACK_WIDTH = 96; // widest pill ("Animate") + gap
  const assetsEndOffset = props.panelSide === 'end' ? (props.panelWidth || 0) + 16 + PANEL_EDGE_STACK_WIDTH : 12;
  const drawerEndOffset = (riskDrawerOpen && drawerSide === 'end') ? RISK_DRAWER_SHIFT : 0;
  // Host-owned drawers on the end side (telemetry side drawer) report their
  // width + gap through `extraEndInset`.
  const clusterEndInset = assetsEndOffset + drawerEndOffset + (props.extraEndInset || 0);
  // With the list on the start side its Flip/Trails/Animate/Snap stack sits
  // there instead; bound the cluster on that side too so a narrow map (list +
  // side telemetry drawer open) wraps the pills onto a second row rather than
  // running them under the stack.
  const clusterStartInset = props.panelSide === 'end' ? 12 : (props.panelWidth || 0) + 16 + PANEL_EDGE_STACK_WIDTH;

  return (
    <div className="relative w-full h-full">
      <div ref={mapContainer} className="w-full h-full" />
      <div
        className={`absolute ${props.controlsTop || 'top-16'} z-10 flex flex-wrap justify-end items-start gap-2 transition-[inset] duration-200`}
        style={{ insetInlineEnd: clusterEndInset, insetInlineStart: clusterStartInset }}
      >
        <button
          onClick={() => setShowPOIs(!showPOIs)}
          className={`h-9 px-3.5 text-xs font-semibold rounded-xl flex items-center gap-2 transition-all shadow-md ${
            showPOIs
              ? 'bg-blue-500 text-white shadow-blue-500/25'
              : 'bg-white/90 text-slate-600 hover:bg-white border border-slate-200/80 dark:bg-slate-800/90 dark:text-slate-300 dark:border-slate-700/80 dark:hover:bg-slate-800'
          }`}
          style={{ backdropFilter: 'blur(12px)' }}
          title="Toggle Points of Interest"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>
          POI
          {poiList.length > 0 && (
            <span className={`text-[10px] px-1.5 py-0.5 rounded-md font-bold ${
              showPOIs ? 'bg-white/20' : 'bg-slate-100 text-slate-500'
            }`}>
              {poiList.length}
            </span>
          )}
        </button>
        <HeatmapControl mapRef={heatmapApiRef} />
        {showRouteControl && (
          <RouteControl mapRef={routeApiRef} />
        )}
        <IncidentsLayerControl />
        <WeatherChip mapRef={mapRef} available={Boolean(layersStatus?.weather?.available)} />
        <MapLayersControl mapRef={mapRef} showLayers={layerTogglesEnabled} layersStatus={layersStatus} />
        <SavedViewsControl mapRef={mapRef} />
      </div>
    </div>
  );
});

export default MapView;
